/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-16 11:36:56
 * @ Modified by: David Helmick
 * @ Modified time: 2025-12-17 19:07:25
 * @ Description: Turnkey Strategy Analysis page
 */

import React, { Suspense, lazy, useEffect, useState } from 'react'
import {
  strategyBadgeIconSx,
  strategyBlockSpacerSx,
  strategyCardBodyPadSx,
  strategyCardTitleSx,
  strategyCircleGreenAvatarSx,
  strategyFieldLabelSx,
  strategyFlexCenterOnlySx,
  strategyFlexCenterSx,
  strategyFlexFullWidthSx,
  strategyFlexPadSx,
  strategyFooterNoteSx,
  strategyFullHeight100Sx,
  strategyFullWidthHiddenSx,
  strategyFullWidthSx,
  strategyIconGap20Style,
  strategyIconGap5Style,
  strategyIconGapStyle,
  strategyItalicMutedSx,
  strategyLinkUnderlineBlueSx,
  strategyMarginBottom10Sx,
  strategyMarginBottom5Sx,
  strategyMarginTop25Sx,
  strategyMarginTop35Sx,
  strategyMarginTopBottom10Sx,
  strategyMaxHeight440Sx,
  strategyMetricGrayBgSx,
  strategyMetricRowFlexSx,
  strategyMetricRowSx,
  strategyMutedDividerSx,
  strategyMutedTextSx,
  strategyNavyBgSx,
  calcDialogPaperSx,
  strategyPad5Sx,
  strategyPadVert15Sx,
  strategySectionLabelSx,
  strategySubtitleCenterSx,
  strategyTableCellRightSx,
  strategyTableRowLastSx,
  strategyTableRowSx,
  strategyUppercaseMetaSx,
  strategyWhiteTextSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { useQuery } from '@apollo/client/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CountUp from '@/utils/SafeCountUp'
import clsx from 'clsx'

// Centralized lodash imports - much cleaner!
import {
  filter,
  map,
  concat,
  sum,
  round,
  every,
  isEmpty,
  includes,
  difference,
  sortBy,
  find,
  findIndex,
  get,
  merge,
  cloneDeep,
  zip,
  slice,
  isNil,
  replace,
  head,
  last,
  compact,
  uniq,
} from '@/utils/lodash'

// Enhanced utilities for common patterns
import {
  findByProperty,
  safeHead,
  groupByProperty,
  sumProperty,
} from '@/utils/lodash/array'

import { safeGet, safeSet, hasProperty } from '@/utils/lodash/object'

const Chart = lazy(() => import('@/utils/SafeApexChart'))

import {
  Grid,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormGroup,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Drawer,
  IconButton,
  FormControl,
  Collapse,
  Select,
} from '@mui/material'

import dark365Square from '@/assets/images/365_square_Dark.png'

import ContactAgent from '../../../Marketplace/ContactAgent'

import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { useMutation } from '@apollo/client/react'
import { Close } from '@mui/icons-material'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { enqueueSnackbar } from 'notistack'
import { GET_CARDS } from '@/housefolios-components/Properties/queries'

import { assumptionsOptions } from '@/housefolios-components/AssumptionsSettings/Accordion' //SPECIFIC COMMENT: Don't remove. Used for rehab card fields
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import { useLocation } from 'react-router'
import useAmortizationDialog from '@/hooks/useAmortizationDialog'
import AmortizationDialog from '@/housefolios-components/Dialogs/Amortization'
import { loanBalance } from '@/utils/loanBalance'

import {
  ContactInformation,
  getCashEntryFee,
  getCreativeEntryFee,
  getPitiTotal,
} from '@/utils/loanBalance'
import SubtoPITICash from '@/housefolios-components/Dialogs/SubtoPITI/Cash/'
import SubtoPITICreative from '@/housefolios-components/Dialogs/SubtoPITI/Creative/'
import CommentsDialog from '@/housefolios-components/Dialogs/Comments'
import {
  TurnkeyAcquisitionExpensesTour,
  TurnkeyCashPositioningTour,
  TurnkeyComparablePropertiesTour,
  TurnkeyDispositionExpensesTour,
  TurnkeyFinancingTour,
  TurnkeyGrossMonthlyIncomeTour,
  TurnkeyMonthlyCashflowTour,
  TurnkeyMonthlyExpensesTour,
  TurnkeyRehabExpensesTour,
  TurnkeyStrategyTour,
  TurnkeyTargetsTour,
  TurnkeyValueOverTimeTour,
} from './tours'

import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'
import {
  ComparablePropertiesCard,
  DeactiveCard,
  StrategyCard,
  StrategyPageBase,
  TargetsCard,
  calculateCompData,
  createChartColors,
  createCurrencyFormat,
  createCashflowChartOptions,
  createDonutChartOptions,
  createPercentageFormat,
  createStrategySummary,
  useStrategyPage,
} from '../Common'

function CenterPageTurnkey(props) {
  const {
    property,
    analysisInput,
    assumptionsInput,
    renderInputFields,
  } = props

  const {
    openAmortization,
    handleOpenAmortization,
    handleCloseAmortization,
  } = useAmortizationDialog()

  const {
    modal1,
    maxOfferDescription,
    openStrategyTour,
    openTargetsTour,
    openComparablePropertiesTour,
    toggle1,
    toggle1Close,
    toggleMaxOffer,
    toggleStrategyTour,
    toggleTargetsTour,
    toggleComparablePropertiesTour,
    closeStrategyTour,
    closeTargetsTour,
    closeComparablePropertiesTour,
  } = useStrategyPage({ strategyName: 'Turnkey'  })

  const [
    openTurnkeyMonthlyCashflowTour,
    setOpenTurnkeyMonthlyCashflowTour,
  ] = useState(false)
  const [
    openTurnkeyAcquisitionExpensesTour,
    setOpenTurnkeyAcquisitionExpensesTour,
  ] = useState(false)
  const [
    openTurnkeyGrossMonthlyIncomeTour,
    setOpenTurnkeyGrossMonthlyIncomeTour,
  ] = useState(false)
  const [
    openTurnkeyMonthlyExpensesTour,
    setOpenTurnkeyMonthlyExpensesTour,
  ] = useState(false)
  const [
    openTurnkeyRehabExpensesTour,
    setOpenTurnkeyRehabExpensesTour,
  ] = useState(false)
  const [openTurnkeyFinancingTour, setOpenTurnkeyFinancingTour] =
    useState(false)
  const [
    openTurnkeyValueOverTimeTour,
    setOpenTurnkeyValueOverTimeTour,
  ] = useState(false)
  const [
    openTurnkeyDispositionExpensesTour,
    setOpenTurnkeyDispositionExpensesTour,
  ] = useState(false)
  const [
    openTurnkeyCashPositioningTour,
    setOpenTurnkeyCashPositioningTour,
  ] = useState(false)

  let results = useQuery(GET_CARDS, {
    variables: { view: 'Turnkey' },
  })
  let data = results.data || {}
  const cardsLoading = results.loading
  const cardsError = results.error
  let { cards = [] } = data

  // const { activeOrg = {} } = props.session.me || {}
  const activeOrg = property?.portfolio?.organization
  const { acquisition = {}, assumptions = {} } = property
  const { analytics = DEFAULT_ANALYTICS, analysis = {} } = acquisition

  const currencyFormat = createCurrencyFormat()
  const currencyFormat2 = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const percentageFormat = createPercentageFormat()
  const pieColors = createChartColors()

  // const loanBalanceAtHoldingPeriodBH = loanBalance({
  //   financeAmount: analytics.turnkey.loanAmount,
  //   mortgagePayment: analytics.turnkey.mp_monthly,
  //   monthlyRate: assumptions.rate / 100 / 12,
  //   holdingPeriod: assumptions.rentalHoldingPeriod * 12,
  //   paydownMonthly: analytics.turnkey.paydownMonthly,
  // })

  let amortizationData = loanBalance({
    financeAmount: analytics.turnkey.loanAmountTurnkey,
    netOperatingIncome: analytics.turnkey.netOperatingIncomeTurnkey,
    mortgagePayment: analytics.turnkey.mortgageTurnkey,
    monthlyRate: assumptions.rateTurnkey / 100 / 12,
    holdingPeriod: assumptions.rentalHoldingPeriodTurnkey * 12,
    payDownAccelerator: assumptions.payDownAccelerator,
    payDownGrowth: assumptions.payDownGrowth,
    growthRateIncome: assumptions.growthRateIncome,
    growthRateExpense: assumptions.growthRateIncome,
  })

  // const [openAmortization, setOpenAmortization] = useState(false)
  // const handleOpenAmortization = () => {
  //   setOpenAmortization(true)
  // }
  // const handleCloseAmortization = () => {
  //   setOpenAmortization(false)
  // }

  const tableHeaders = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Interest', align: 'center' },
    { title: '', name: 'Principal', align: 'center' },
    { title: '', name: 'Additional Principal', align: 'center' },
    { title: '', name: 'Balance', align: 'center' },
  ]

  const dollarPercentAmount = (ofAmount, value, peramt) => {
    let typeData = []
    let dollarAmount = 0
    let percentAmount = 0

    if (peramt === '%') {
      dollarAmount = (value / 100) * ofAmount
      percentAmount = value
    } else if (peramt === '$') {
      dollarAmount = value
      if (ofAmount !== 0)
        percentAmount = ((value / ofAmount) * 100).toFixed(2)
    }

    typeData.push({
      dollar: dollarAmount,
      percent: percentAmount,
    })

    return typeData
  }

  let IRRDataFirst = {}
  let IRRData = []
  let IRRDataLast = {}

  const IRRTable = (args) => {
    let {
      valueAppreciation, //arv ? arv : sellPrice
      grossAnnualRent,
      annualExpenses,
      mp_monthly,
      rentalHoldingPeriod,
      growthRateIncomeMonthly,
      growthRateExpenseMonthly,
      rehabPeriod,
      totalOutOfPocket,
      homePriceAppreciation,
      listPriceToSalePriceRatio,
      salesCommission,
      salesCommissionType,
      sellingClosingCostDollar,
      loanBalanceAtHoldingPeriod,
      dispositionFee,
      dispositionFeeType,
    } = args

    let cashFlowTotal = 0
    let arrInHoldingPeriodMonthly = grossAnnualRent / 12
    let expensesInHoldingPeriodMonthly = annualExpenses / 12
    let cashFlowInHoldingPeriodMonthly = 0
    let salePrice = 0

    let month
    let year
    let monthYear

    const today = new Date()

    const IRRDate = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    )

    for (let index = 1; index < rentalHoldingPeriod + 1; index++) {
      if (index > 1) {
        arrInHoldingPeriodMonthly *= 1 + growthRateIncomeMonthly
        expensesInHoldingPeriodMonthly *= 1 + growthRateExpenseMonthly
      }
      let income =
        index > rehabPeriod + 1 ? arrInHoldingPeriodMonthly : 0
      cashFlowInHoldingPeriodMonthly =
        mp_monthly < 0
          ? income - expensesInHoldingPeriodMonthly
          : income - expensesInHoldingPeriodMonthly - mp_monthly
      if (index === 1) {
        month = IRRDate.toLocaleString('default', { month: 'long'  })
        year = IRRDate.getFullYear().toString()
        monthYear = month + ' ' + year
        IRRDataFirst = {
          totalOutOfPocket,
          monthYear,
        }
      } else {
        valueAppreciation *= 1 + homePriceAppreciation / 12 / 100
        IRRDate.setMonth(IRRDate.getMonth() + 1)
        let month = IRRDate.toLocaleString('default', {
          month: 'long',
        })
        let year = IRRDate.getFullYear().toString()
        monthYear = month + ' ' + year
        if (index < rentalHoldingPeriod) {
          IRRData.push({
            flow: cashFlowInHoldingPeriodMonthly,
            income,
            expenses: expensesInHoldingPeriodMonthly,
            mortgage: mp_monthly,
            monthYear,
          })
        } else if (index === rentalHoldingPeriod) {
          let cashFlowInHoldingPeriod = cashFlowTotal

          salePrice =
            (listPriceToSalePriceRatio / 100) * valueAppreciation
          let salesCommissionTotal =
            salesCommissionType === '$'
              ? salesCommission
              : (salesCommission / 100) * salePrice

          const dispositionFeeIRRDP = dollarPercentAmount(
            salePrice,
            dispositionFee,
            dispositionFeeType,
          )

          const lastMonthCashFlow =
            salePrice -
            loanBalanceAtHoldingPeriod -
            salesCommissionTotal -
            sellingClosingCostDollar -
            dispositionFeeIRRDP[0].dollar +
            cashFlowInHoldingPeriodMonthly

          IRRDataLast = {
            lastMonthCashFlow,
            monthYear,
          }
        }
      }
    }
  }

  IRRTable({
    valueAppreciation: analytics.turnkey.arvTotalTurnkey
      ? analytics.turnkey.arvTotalTurnkey
      : analysis.resellPriceTurnkey,
    grossAnnualRent: sum(analytics.grossAnnualRent),
    annualExpenses: analytics.turnkey.annualExpensesTurnkey,
    mp_monthly: analytics.turnkey.mortgageTurnkey,
    rentalHoldingPeriod: assumptions.rentalHoldingPeriodTurnkey,
    growthRateIncomeMonthly: assumptions.growthRateIncome / 12 / 100,
    growthRateExpenseMonthly:
      assumptions.growthRateExpense / 12 / 100,
    rehabPeriod: -1,
    totalOutOfPocket: analytics.turnkey.totalOutOfPocketTurnkey,
    homePriceAppreciation: assumptions.homePriceAppreciation,
    listPriceToSalePriceRatio: assumptions.listPriceToSalePriceRatio,
    salesCommission: assumptions.salesCommissionTurnkey,
    salesCommissionType: assumptions.salesCommissionTurnkeyType,
    sellingClosingCostDollar:
      analytics.turnkey.sellingClosingCostDollarTurnkey,
    loanBalanceAtHoldingPeriod:
      analytics.turnkey.loanBalanceAtHoldingPeriodTurnkey,
    dispositionFee: assumptions.dispositionFeeTurnkey,
    dispositionFeeType: assumptions.dispositionFeeTurnkeyType,
  })

  const IRRTableFirstHeaders = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Total Out of Pocket', align: 'center' },
  ]

  const IRRTableHeaders = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Income', align: 'center' },
    { title: '', name: 'Expenses', align: 'center' },
    { title: '', name: 'Mortgage', align: 'center' },
    { title: '', name: 'Cashflow', align: 'center' },
  ]

  const IRRTableLastHeaders = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Last Month Cashflow', align: 'center' },
  ]

  const [openManagementCompanies, setOpenManagementCompanies] =
    useState(false)
  const handleOpenManagementCompanies = () => {
    setOpenManagementCompanies(true)
  }
  const handleCloseManagementCompanies = () => {
    setOpenManagementCompanies(false)
  }

  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.checked  })
  }

  //const [loader, setLoader] = useState(false)
  const [dealSauceView, setDealSauceView] = useState('creative')
  const [openPITICashData, setOpenPITICashData] = useState(false)
  const [openPITICreativeData, setOpenPITICreativeData] =
    useState(false)

  const pitiTotal = getPitiTotal(property)
  const creativeEntryFee = getCreativeEntryFee(property)
  const cashEntryFee = getCashEntryFee(property)

  const [accordion, setAccordion] = useState(-1)

  const toggleAccordion = (index) => {
    if (index === accordion) setAccordion(-1)
    else setAccordion(index)
  }

  const [getFinancing, setGetFinancing] = useState(null)

  const toggleGetFinancing = (name) => {
    setGetFinancing(name)
  }

  const toggleGetFinancingClose = () => {
    setGetFinancing(null)
  }

  const cashFlowAccumulationCalculate = (args) => {
    const {
      accelerator,
      term,
      rentAmount,
      vacancy,
      propertyTaxes,
      homeInsurance,
      monthlyHOA,
      propertyMgmtPercent,
      maintenance,
      utilities,
      annualRevenueIncrease,
      annualOperatingExpenseIncrease,
      //added so that cash flow is with Financing
      mortgagePayment,
    } = args

    let pdAccelerator = accelerator / 100
    let rent = rentAmount
    let rentIncrease = map(rent, (value) => 0)
    let vac = map(
      zip(rentAmount, vacancy),
      ([rentAmount, vacancy]) => rentAmount * vacancy,
    )
    let revenue = 0
    let propTax = propertyTaxes
    let propTaxIncrease = 0
    let insurance = homeInsurance
    let insuranceIncrease = 0
    let hoa = monthlyHOA
    let hoaIncrease = 0
    let propMgmt = sum(rentAmount) * propertyMgmtPercent
    let main = sum(rentAmount) * maintenance
    let mainIncrease = 0
    let uti = utilities
    let utiIncrease = 0
    let expenses = 0
    let cashFlow = 0
    let cfyear = []
    //added so that cash flow is with Financing
    let mortgage = 0

    for (let index = 1; index < term; index++) {
      if (index > 1 && index < 12) {
        rent = map(
          zip(rent, rentAmount),
          ([rent, rentAmount]) => rent + rentAmount,
        )
        propTax += propertyTaxes
        insurance += homeInsurance
        hoa += monthlyHOA
        //added so that cash flow is with Financing
        mortgage += mortgagePayment
      } else if (index % 12 === 0) {
        if (index / 12 === 1) {
          rent = map(
            zip(rent, rentAmount),
            ([rent, rentAmount]) => rent + rentAmount,
          )
          vac = map(
            zip(rent, vacancy),
            ([rent, vacancy]) => rent * vacancy,
          )
          revenue = sum(rent) - sum(vac)

          propTax += propertyTaxes
          insurance += homeInsurance
          hoa += monthlyHOA
          propMgmt = revenue * propertyMgmtPercent
          main = sum(rent) * maintenance
          expenses = propTax + insurance + hoa + propMgmt + main + uti

          //added so that cash flow is with Financing
          mortgage += mortgagePayment

          cashFlow = revenue - expenses - mortgage
          cashFlow = cashFlow - pdAccelerator * cashFlow

          cfyear.push({
            year: index / 12,
            cashFlow: cashFlow,
          })

          rentIncrease = map(
            rentAmount,
            (rentAmount) =>
              rentAmount + rentAmount * annualRevenueIncrease,
          )
          propTaxIncrease =
            propTax * (1 + annualOperatingExpenseIncrease)
          insuranceIncrease =
            insurance * (1 + annualOperatingExpenseIncrease)
          hoaIncrease = hoa * (1 + annualOperatingExpenseIncrease)
          mainIncrease = main * (1 + annualOperatingExpenseIncrease)
          utiIncrease = uti * (1 + annualOperatingExpenseIncrease)
        } else {
          rent = map(
            zip(rent, rentIncrease),
            ([rent, rentIncrease]) => rent + rentIncrease,
          )
          vac = map(
            zip(rent, vacancy),
            ([rent, vacancy]) => rent * vacancy,
          )
          revenue = sum(rent) - sum(vac)

          propTax += propTaxIncrease
          insurance += insuranceIncrease
          hoa += hoaIncrease
          propMgmt = revenue * propertyMgmtPercent
          main += mainIncrease
          uti += utiIncrease
          expenses = propTax + insurance + hoa + propMgmt + main + uti

          //added so that cash flow is with Financing
          mortgage += mortgagePayment

          cashFlow = revenue - expenses - mortgage
          cashFlow = cashFlow - pdAccelerator * cashFlow

          cfyear.push({
            year: index / 12,
            cashFlow: cashFlow,
          })

          rentIncrease = map(
            rentIncrease,
            (rentIncrease) =>
              rentIncrease + rentIncrease * annualRevenueIncrease,
          )
          propTaxIncrease =
            propTaxIncrease * (1 + annualOperatingExpenseIncrease)
          insuranceIncrease =
            insuranceIncrease * (1 + annualOperatingExpenseIncrease)
          hoaIncrease =
            hoaIncrease * (1 + annualOperatingExpenseIncrease)
          mainIncrease =
            mainIncrease * (1 + annualOperatingExpenseIncrease)
          utiIncrease =
            utiIncrease * (1 + annualOperatingExpenseIncrease)
        }
      } else {
        rent = map(
          zip(rent, rentIncrease),
          ([rent, rentIncrease]) => rent + rentIncrease,
        )
        //added so that cash flow is with Financing
        mortgage += mortgagePayment
      }
    }

    return cfyear
  }

  const cashFlowAccumulation = cashFlowAccumulationCalculate({
    accelerator: assumptions.payDownAccelerator || 0,
    term:
      assumptions.termTurnkeyType === 'Years'
        ? (assumptions.termTurnkey || 0) * 12 + 1
        : (assumptions.termTurnkey || 0) + 1,
    rentAmount: analysis.rent || [0],
    vacancy: assumptions.vacancy
      ? map(assumptions.vacancy, (vacancy) => vacancy / 100)
      : [0],
    propertyTaxes: (analysis.propertyTaxes || 0) / 12,
    homeInsurance:
      (analytics.turnkey.propertyInsuranceDollarTurnkey || 0) / 12,
    monthlyHOA: analytics.turnkey.hoaTotal / 12 || 0,
    propertyMgmtPercent:
      (assumptions.propertyManagementTurnkey || 0) / 100,
    maintenance: (assumptions.maintenanceTurnkey || 0) / 100,
    utilities: analytics.turnkey.utilitiesTypeDollarTurnkey || 0,
    annualRevenueIncrease: (assumptions.growthRateIncome || 0) / 100,
    annualOperatingExpenseIncrease:
      (assumptions.growthRateExpense || 0) / 100,
    mortgagePayment: analytics.turnkey.mortgageTurnkey || 0,
  })

  let cashFlow1 = 0
  let cashFlow3 = 0
  let cashFlow5 = 0
  let cashFlow10 = 0

  for (let key in cashFlowAccumulation) {
    const tmp = cashFlowAccumulation[key]
    if (tmp.year === 1) cashFlow1 = tmp.cashFlow
    if (tmp.year === 3) cashFlow3 = tmp.cashFlow
    if (tmp.year === 5) cashFlow5 = tmp.cashFlow
    if (tmp.year === 10) cashFlow10 = tmp.cashFlow
  }

  const options = {
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: ['1 yr', '3 yrs', '5 yrs', '10 yrs'],
    },
    yaxis: {
      labels: {
        formatter: currencyFormat.format,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w  }) {
        return (
          '<div class="arrow_box">' +
          '<span>' +
          //[w.globals.labels][dataPointIndex] +
          ['1 yr', '3 yrs', '5 yrs', '10 yrs'][dataPointIndex] +
          ': ' +
          currencyFormat.format(series[seriesIndex][dataPointIndex]) +
          '</span>' +
          '</div>'
        )
      },
    },
  }
  const series = [
    {
      data: [
        round(cashFlow1) || 0,
        round(cashFlow3) || 0,
        round(cashFlow5) || 0,
        round(cashFlow10) || 0,
      ],
    },
  ]

  const basicAcquisitionFields = [
    {
      type: 'dollar',
      name: 'resellPriceTurnkey',
      label: 'New Asking Price',
      value:
        analysisInput.resellPriceTurnkey === null
          ? ''
          : analysisInput.resellPriceTurnkey.toString(),
      helper:
        !analysisInput.resellPriceTurnkey ||
        analysisInput.resellPriceTurnkey === '0'
          ? '0% Discount to ARV'
          : (
              (1 -
                analysisInput.resellPriceTurnkey /
                  analytics.turnkey.arvTotalTurnkey) *
              100
            ).toFixed(0) + '% Discount to ARV',
      xs: 12,
      lg: 6,
      tooltipField:
        'The price that you, the investor, will ask for when you are ready to sell the property.',
      dataTour: 'acquisitionExpenses-step-3',
    },
    {
      type: 'dollar',
      name: 'turnkeyOfferPrice',
      label: 'End Buyer Purchase Price',
      value:
        analysisInput.turnkeyOfferPrice == null
          ? ''
          : analysisInput.turnkeyOfferPrice.toString(),
      helper:
        !analysisInput.turnkeyOfferPrice ||
        analysisInput.turnkeyOfferPrice === '0'
          ? '0% Discount to Price'
          : (
              (1 -
                analysisInput.turnkeyOfferPrice /
                  analysisInput.listPrice) *
              100
            ).toFixed(0) + '% Discount to Price',
      xs: 12,
      lg: 6,
      tooltipField:
        'The price that the end buyer will pay for the home.',
      dataTour: 'acquisitionExpenses-step-4',
    },
    {
      type: 'dollar',
      name: includes(
        assumptionsInput.strategySpecificFields,
        'afterRepairValue',
      )
        ? 'afterRepairValueTurnkey'
        : 'afterRepairValue',
      label: 'After Repair Value',
      tooltipButtonText: 'Show ARV data',
      value: includes(
        assumptionsInput.strategySpecificFields,
        'afterRepairValue',
      )
        ? analysisInput.afterRepairValueTurnkey === null
          ? ''
          : analysisInput.afterRepairValueTurnkey.toString()
        : analysisInput.afterRepairValue === null
          ? ''
          : analysisInput.afterRepairValue.toString(),
      helper:
        currencyFormat.format(analytics.turnkey.equityTurnkey) +
        ' in equity',
      xs: 12,
      lg: 6,
      definition: 'afterRepairValue',
      definitionTarget: includes(
        assumptionsInput.strategySpecificFields,
        'afterRepairValue',
      )
        ? 'afterRepairValueTurnkey'
        : 'afterRepairValue',
      tooltipField:
        'The ARV is the value of a property after you have conducted repairs and are ready to sell.',
      dataTour: 'acquisitionExpenses-step-5',
    },
    {
      type: 'dropdown',
      name: 'closingCostTurnkey',
      value: assumptionsInput.closingCostTurnkey,
      label: 'Closing Cost',
      adornmentToggle: assumptionsInput.closingCostTurnkeyType,
      adornment: assumptionsInput.closingCostTurnkeyType,
      adornmentName: 'closingCostTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Fees (attorney/title fees, etc) Paid at Closing. Expressed as a dollar amount or precentage of the purchase prices.',
      dataTour: 'acquisitionExpenses-step-6',
    },
    {
      type: 'dollar',
      name: 'creditFromSeller',
      value: assumptionsInput.creditFromSeller,
      label: 'Credit From Seller',
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'A seller credit is the dollar amount the seller will put towards your closing costs.',
      dataTour: 'acquisitionExpenses-step-7',
    },
  ]

  const advancedAcquisitionFields = [
    {
      type: 'dropdown',
      name: 'acquisitionCommission',
      label: 'Acq Commission',
      value: assumptionsInput.acquisitionCommission,
      adornmentToggle: assumptionsInput.acquisitionCommissionType,
      adornment: assumptionsInput.acquisitionCommissionType,
      adornmentName: 'acquisitionCommissionType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Advanced Fee - Used in addition to sales of typical sales commissions expressed as a percentage of the Purchase price or a fixed dollar amount.',
    },
    {
      type: 'dollar',
      name: 'acquisitionFee',
      label: 'Acquisition Fee',
      value: assumptionsInput.acquisitionFee,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Advanced Fee - Used in addition to sales of typical sales commissions expressed as a fixed dollar amount',
    },
    // {
    //   type: 'percentage',
    //   name: 'assetManagementFee',
    //   label: 'Asset Mgmt Fee',
    //   value: assumptionsInput.assetManagementFee,
    //   xs: 6,
    //   assumptions: true,
    // },
    {
      type: 'dropdown',
      name: 'financingFeeTurnkey',
      label: 'Financing Fee',
      value: assumptionsInput.financingFeeTurnkey,
      adornment: assumptionsInput.financingFeeTurnkeyType,
      adornmentToggle: assumptionsInput.financingFeeTurnkeyType,
      adornmentName: 'financingFeeTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Advanced Fee - A fee that the real estate professional charges for securing financing on the property. Expressed as a percentage of the loan amount.',
    },
    {
      type: 'dollar',
      name: 'ownerReservesTurnkey',
      label: '1x Owner Reserve',
      value: assumptionsInput.ownerReservesTurnkey,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'A one-time reserve you set aside (ex: $5,000) to cover unforeseen repairs, etc. This amount is in addition to the monthly capex (reserve) you set aside each month.',
    },
  ]

  const propertyManagementCompanies = [
    {
      image:
        'https://assets-global.website-files.com/5ebdcacf6fe55d6b95c4f2a1/5f244161238395a76fe1c352_logo-dark-vertical.svg',
      name: 'Mynd',
      // link: 'https://www.mynd.co/partners/housefolios',
      link: 'https://www.mynd.co',
      value: 'mynd',
    },
    {
      image: dark365Square,
      name: 'home365',
      link: 'https://www.home365.co',
      value: 'home365',
    },
    {
      image: '/images/guardianAsset.jpeg',
      name: 'Guardian Asset',
      link: 'https://guardianassetmgt.com/Home/Contact',
      value: 'guardianAsset',
    },
  ]

  const customAcquisitonField = [
    {
      type: 'budgetLines',
      name: 'customAcquisitionExpenses',
      value: analysisInput.customAcquisitionExpenses,
      label: 'Custom Acquisition Expenses',
      options: activeOrg.customAcquisitionExpenseTypes,
      xs: 12,
      optionName: 'customAcquisitionExpenseTypes',
      removeOption: true,
    },
  ]

  const uncheckAdvanced = every(
    advancedAcquisitionFields,
    (field) => !field.value,
  )

  const [state, setState] = React.useState({
    checkedA: !uncheckAdvanced,
    checkedB: false,
    checkedF: false,
    checkedG: false,
  })

  const acquisitionChartOptions = createDonutChartOptions(
    currencyFormat,
    concat(
      [
        'End Buyer Purchase Price',
        'Closing Costs',
        'Acquisition Commission',
        'Acquisition Fee',
        /*'Asset Mangement Fee',*/ 'Points',
        'Financing Fee',
        'One-time Owner Reserve',
      ],
      map(
        analysis.customAcquisitionExpenses,
        (expense) => expense.type,
      ),
    ),
    pieColors,
  )
  const acquisitionChartSeries = concat(
    [
      round(analysis.turnkeyOfferPrice, 2) || 0.0001,
      round(analytics.turnkey.closeCostDollarTurnkey, 2) || 0,
      round(analytics.acquisitionCommissionDollarTurnkey, 2) || 0,
      round(assumptions.acquisitionFee, 2) || 0,
      /*round(analytics.turnkey.assetManagementFeeTotal, 2) || 0,*/ round(
        analytics.turnkey.pointsAmountTurnkey,
        2,
      ) || 0,
      round(analytics.turnkey.financingFeeTotalTurnkey, 2) || 0,
      round(assumptions.ownerReservesTurnkey, 2) || 0,
    ],
    map(
      analysis.customAcquisitionExpenses,
      (expense) => round(expense.value, 2) || 0,
    ),
  )

  const grossMonthlyIncomeFields = filter(
    [
      {
        type: 'select',
        name: 'rentType',
        label: 'Rent Type',
        value: assumptionsInput.rentType,
        options: assumptionsOptions(activeOrg)?.rentTypes,
        xs: 12,
        lg: 12,
        assumptions: true,
      },
      {
        type: 'dollar',
        name: `rent.${props.activeUnit}`,
        label: 'Total Rental Income',
        tooltipButtonText: 'Show Total Rent data',
        value:
          analysisInput.rent[props.activeUnit] === null
            ? ''
            : analysisInput.rent[props.activeUnit].toString(),
        helper:
          property.sqft &&
          property.sqft[props.activeUnit] &&
          analysisInput.rent[props.activeUnit]
            ? currencyFormat2.format(
                analysisInput.rent[props.activeUnit] /
                  property.sqft[props.activeUnit],
              ) + ' rent per sqft'
            : '$0.00 rent per sqft',
        xs: 12,
        lg: 6,
        definition: 'rent',
        // disabled: !isNil(analysisInput.ownerOccupiedTurnkey) && props.activeUnit === analysisInput.ownerOccupiedTurnkey,
        tooltipField:
          "A tenant's monthly payment to a landlord for the use of property or land.",
        hide: assumptionsInput.rentType !== 'singleRent',
        dataTour: 'grossMonthlyIncome-step-1',
      },
      {
        type: 'dollar',
        name: `rentLow.${props.activeUnit}`,
        label: 'Rent Range Low',
        tooltipButtonText: 'Show Total Rent data',
        value:
          analysisInput.rentLow[props.activeUnit] === null
            ? ''
            : analysisInput.rentLow[props.activeUnit].toString(),
        xs: 12,
        lg: 6,
        definition: 'rent',
        tooltipField: 'Lower end of projected rent',
        hide: assumptionsInput.rentType !== 'rentRange',
        dataTour: '',
      },
      {
        type: 'dollar',
        name: `rentHigh.${props.activeUnit}`,
        label: 'Rent Range High',
        tooltipButtonText: 'Show Total Rent data',
        value:
          analysisInput.rentHigh[props.activeUnit] === null
            ? ''
            : analysisInput.rentHigh[props.activeUnit].toString(),
        xs: 12,
        lg: 6,
        definition: 'rent',
        tooltipField: 'Higher end of projected rent',
        hide: assumptionsInput.rentType !== 'rentRange',
        dataTour: '',
      },
      {
        type: 'percentage',
        name: `vacancy.${props.activeUnit}`,
        label: 'Vacancy Rate',
        value: assumptionsInput.vacancy[props.activeUnit],
        xs: 12,
        lg: 6,
        assumptions: true,
        tooltipField:
          'The percentage of time that a property is not rented out during the course of a year.',
        dataTour: 'grossMonthlyIncome-step-2',
      },
      {
        type: 'dollar',
        name: `otherIncome.${props.activeUnit}`,
        label: 'Other Monthly Income',
        value:
          analysisInput.otherIncome[props.activeUnit] === null
            ? ''
            : analysisInput.otherIncome[props.activeUnit].toString(),
        xs: 12,
        lg: 6,
        tooltipField:
          'Additional income you earn through the property. Some examples might be coin-op laundry, parking, storage, etc.',
        dataTour: 'grossMonthlyIncome-step-3',
      },
    ],
    (field) => !field.hide,
  )

  const grossMonthlyIncomeChartOptions = createDonutChartOptions(
    currencyFormat,
    concat(
      map(
        analytics.grossAnnualRent,
        (value, index) => `Unit ${index + 1} Monthly Rental Income`,
      ),
      map(
        analysis.otherIncome,
        (value, index) => `Unit ${index + 1} Other Monthly Income`,
      ),
    ),
    pieColors,
  )
  const grossMonthlyIncomeChartSeries = concat(
    map(
      analytics.grossAnnualRent,
      (grossAnnualRent, index) =>
        round(grossAnnualRent / 12, 2) || (index === 0 ? 0.0001 : 0),
    ),
    map(
      analysis.otherIncome,
      (otherIncome) => round(otherIncome, 2) || 0,
    ),
  )

  // Before: Multiple find operations with fallbacks
  // const expenseCard =
  //   find(
  //     cards,
  //     (card) =>
  //       card.name === 'expenses' && card.member === activeOrg.member,
  //   ) ||
  //   find(
  //     cards,
  //     (card) => card.name === 'expenses' && card.member === null,
  //   ) ||
  //   {}

  // After: Using enhanced utilities (could be refactored to)
  // const expenseCard = findByProperty(cards, 'name', 'expenses') || {}

  const expenseCard =
    find(
      cards,
      (card) =>
        card.name === 'expenses' && card.member === activeOrg.member,
    ) ||
    find(
      cards,
      (card) => card.name === 'expenses' && card.member === null,
    ) ||
    {}
  const monthlyExpensesFields = map(
    get(expenseCard, 'card.fields'),
    (field) => {
      const input = field.analytics
        ? analytics.turnkey
        : field.assumptions
          ? assumptionsInput
          : analysisInput
      const name = field.suffix
        ? includes(field.name, 'Type')
          ? replace(field.name, 'Type', 'TurnkeyType')
          : field.name + 'Turnkey'
        : field.name
      const adornmentName =
        field.suffix && field.adornmentName
          ? includes(field.adornmentName, 'Type')
            ? replace(field.adornmentName, 'Type', 'TurnkeyType')
            : field.adornmentName + 'Turnkey'
          : field.adornmentName
      const definition =
        field.suffix && field.definition
          ? includes(field.definition, 'Type')
            ? replace(field.definition, 'Type', 'TurnkeyType')
            : field.definition + 'Turnkey'
          : field.definition
      // Before: Multiple get operations with fallbacks
      // const options =
      //   field.options && field.assumptions
      //     ? get(assumptionsOptions(activeOrg), field.options) ||
      //       get(activeOrg, field.options) ||
      //       field.options
      //     : get(activeOrg, field.options) || field.options

      // After: Using enhanced utilities (could be refactored to)
      // const options = safeGet(assumptionsOptions(activeOrg), field.options) ||
      //                 safeGet(activeOrg, field.options) ||
      //                 field.options

      const options =
        field.options && field.assumptions
          ? get(assumptionsOptions(activeOrg), field.options) ||
            get(activeOrg, field.options) ||
            field.options
          : get(activeOrg, field.options) || field.options
      return {
        ...field,
        name,
        value: get(input, name),
        adornmentToggle: get(input, adornmentName),
        adornment: get(input, adornmentName),
        adornmentName: adornmentName,
        definition: definition,
        options: options,
      }
    },
  )

  const monthlyExpensesChartOptions = createDonutChartOptions(
    currencyFormat,
    concat(
      [
        'Property Insurance',
        'Property Mgmt Fee',
        'Monthly HOA',
        'Property Taxes',
        'Maintenance',
        'Utilities',
        'Capex',
        'Other Expenses',
        'Mortgage',
      ],
      map(analysis.customMonthlyExpenses, (expense) => expense.type),
    ),
    pieColors,
  )
  const monthlyExpensesChartSeries = concat(
    [
      round(
        analytics.turnkey.propertyInsuranceDollarTurnkey / 12,
        2,
      ) || 0.0001,
      round(
        analytics.turnkey.propertyManagementDollarTurnkey / 12,
        2,
      ) || 0,
      round(analytics.turnkey.hoaTotal / 12, 2) || 0,
      round(analysis.propertyTaxes / 12, 2) || 0,
      round(analytics.turnkey.maintenanceTurnkeyDollar / 12, 2) || 0,
      round(analytics.turnkey.utilitiesTypeDollarTurnkey, 2) || 0,
      round(analytics.turnkey.monthlyReservesTypeDollarTurnkey, 2) ||
        0,
      round(assumptions.otherExpenseTurnkey / 12, 2) || 0,
      round(analytics.turnkey.mortgageTurnkey, 2) || 0,
    ],
    map(
      analysis.customMonthlyExpenses,
      (expense) => round(expense.value, 2) || 0,
    ),
  )

  // const rehabCard =
  //   find(
  //     cards,
  //     (card) =>
  //       card.name === 'rehab' && card.member === activeOrg.member,
  //   ) ||
  //   find(
  //     cards,
  //     (card) => card.name === 'rehab' && card.member === null,
  //   ) ||
  //   {}
  // const rehabFields = filter(
  //   map(get(rehabCard, 'card.fields'), (field) => {
  //     const input = field.analytics
  //       ? analytics.turnkey
  //       : field.assumptions
  //       ? assumptionsInput
  //       : analysisInput
  //     const options =
  //       field.options && field.assumptions
  //         ? get(assumptionsOptions(activeOrg), field.options) ||
  //           get(activeOrg, field.options) ||
  //           field.options
  //         : get(activeOrg, field.options) || field.options
  //     const memberOptions =
  //       field.memberOptions && field.assumptions
  //         ? get(assumptionsOptions(activeOrg), field.memberOptions) ||
  //           get(activeOrg, field.memberOptions)
  //         : get(activeOrg, field.memberOptions)
  //     const name = field.suffix
  //       ? includes(field.name, 'Type')
  //         ? replace(field.name, 'Type', 'TurnkeyType')
  //         : field.name + 'Turnkey'
  //       : field.name
  //     const hide =
  //       field.suffix && field.hide
  //         ? includes(field.hide, 'Type')
  //           ? replace(field.hide, 'Type', 'TurnkeyType')
  //           : field.hide + 'Turnkey'
  //         : field.hide
  //     const adornmentName =
  //       field.suffix && field.adornmentName
  //         ? includes(field.adornmentName, 'Type')
  //           ? replace(field.adornmentName, 'Type', 'TurnkeyType')
  //           : field.adornmentName + 'Turnkey'
  //         : field.adornmentName
  //     const definition =
  //       field.suffix && field.definition
  //         ? includes(field.definition, 'Type')
  //           ? replace(field.definition, 'Type', 'TurnkeyType')
  //           : field.definition + 'Turnkey'
  //         : field.definition
  //     const helper = includes(field.name, 'rehabTier')
  //       ? currencyFormat.format(
  //           (field.value ? eval(field.value) : get(input, name)) *
  //             (assumptionsInput.percentageOfARVFixFlip / 100) *
  //             propertyInput.sqft,
  //         ) + ' Total'
  //       : null
  //     return {
  //       ...field,
  //       name,
  //       value: get(input, name),
  //       adornmentToggle: get(input, adornmentName),
  //       adornment: get(input, adornmentName),
  //       adornmentName: adornmentName,
  //       definition: definition,
  //       options: filter(
  //         options,
  //         (option) =>
  //           isEmpty(option.showMarkets) ||
  //           includes(option.showMarkets, analysisInput.marketType),
  //       ),
  //       memberOptions: filter(
  //         memberOptions,
  //         (option) =>
  //           isEmpty(option.showMarkets) ||
  //           includes(option.showMarkets, analysisInput.marketType),
  //       ),
  //       helper,
  //       hide,
  //     }
  //   }),
  //   (field) => !field.hide || !eval(field.hide),
  // )

  const rehabFields = filter(
    [
      {
        type: 'select',
        name: 'rehabTurnkeyType',
        value: assumptionsInput.rehabTurnkeyType,
        label: 'Rehab Type',
        xs: 12,
        options: assumptionsOptions(activeOrg)?.rehabOptions,
        assumptions: true,
      },
      {
        type: 'dollar',
        name: 'rehabCostTurnkey',
        value: analysisInput.rehabCostTurnkey,
        label: 'Est End Rehab',
        tooltipButtonText: 'Show Estimated Rehab data',
        xs: 12,
        lg: 6,
        definition: 'rehabCostTurnkey',
        tooltipField:
          'An expense that is incurred when restoring an entity to its normal or near-normal condition.',
        hide:
          assumptionsInput.rehabTurnkeyType !== 'singleRehabValue',
        dataTour: 'rehabExpenses-step-2',
      },
      {
        type: 'budgetLines',
        name: 'detailedRehabCostTurnkey',
        value: analysisInput.detailedRehabCostTurnkey,
        label: 'End Rehab Costs',
        xs: 12,
        options: [
          'Appliances',
          'Cabinets/Vanities',
          'Carpet',
          'Countertops/Sinks/Toilets',
          'Driveway/Decking/Patio',
          'Electrical',
          'Exterior',
          'Flooring',
          'Framing',
          'Insulation/Drywall',
          'Landscaping',
          'Masonry',
          'Mechanical',
          'Molding/Tile/Trim',
          'Paint',
          'Plans/Permits',
          'Plumbing',
          'Roof',
          'Siding/Gutters',
          'Site Prep',
          'Staging',
          'Windows/Doors',
          'Other',
        ],
        hide: assumptionsInput.rehabTurnkeyType !== 'detailedRehab',
        dataTour: 'rehabExpenses-step-2',
      },
      {
        type: 'rehabCalc',
        name: 'rehabCalcTotalTurnkey',
        label: 'Rehab Calculator',
        value: analysisInput.rehabCalcTotalTurnkey,
        xs: 12,
        lg: 6,
        property: property,
        assumptionsInput: assumptionsInput,
        affiliate: props.affiliate,
        hide: assumptionsInput.rehabTurnkeyType !== 'rehabCalc',
        dataTour: 'rehabExpenses-step-2',
      },
      {
        type: 'select',
        name: 'rehabTierTurnkey',
        value: assumptionsInput.rehabTierTurnkey,
        label: 'Rehab Tier',
        tooltipButtonText: 'Show Estimated Rehab data',
        xs: 12,
        lg: 6,
        options: filter(
          activeOrg.rehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        optionName: 'rehabTiers',
        memberOptions: filter(
          activeOrg.memberRehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        dynamicMemberOptions: map(
          filter(
            activeOrg.dynamicMemberRehabTiers,
            (option) =>
              isEmpty(option.showMarkets) ||
              includes(
                option.showMarkets,
                analysisInput.marketType ||
                  props.affiliate?.defaultMarketType,
              ),
          ),
          (option) => {
            let breakPointIndex = 0
            const arv = includes(
              assumptionsInput.strategySpecificFields,
              'afterRepairValue',
            )
              ? analysisInput.afterRepairValueTurnkey === null
                ? null
                : analysisInput.afterRepairValueTurnkey
              : analysisInput.afterRepairValue === null
                ? null
                : analysisInput.afterRepairValue
            while (
              option?.breakpoints[breakPointIndex] < arv &&
              breakPointIndex < option?.breakpoints.length
            ) {
              breakPointIndex++
            }
            return {
              ...option,
              label: `$${option.values[breakPointIndex]}/Sqft`,
            }
          },
        ),
        removeOption: true,
        assumptions: true,
        hide:
          assumptionsInput.rehabTurnkeyType !== 'rehabTier' &&
          assumptionsInput.rehabTurnkeyType !==
            'flippingAmericaRehab',
        dataTour: 'rehabExpenses-step-2',
        helper: currencyFormat.format(
          analytics.turnkey.rehabTierValueTurnkey,
        ),
      },
      {
        name: 'marketType',
        value: !analysisInput.marketType
          ? props.affiliate?.defaultMarketType
          : analysisInput.marketType,
        label: 'Market Type',
        options: compact(
          concat(
            [props.affiliate?.defaultMarketType],
            props.affiliate?.marketTypes,
          ),
        ),
        type: 'select',
        xs: 12,
        lg: 6,
        hide:
          isEmpty(props.affiliate?.marketTypes) ||
          (assumptionsInput.rehabTurnkeyType !== 'rehabTier' &&
            assumptionsInput.rehabTurnkeyType !==
              'flippingAmericaRehab'),
      },
      {
        type: 'dollar',
        name: 'rehabDecadeCostTurnkey',
        value: assumptionsInput.rehabDecadeCostTurnkey,
        label: 'Rehab Per Decade',
        xs: 12,
        lg: 6,
        hide:
          assumptionsInput.rehabTurnkeyType !==
          'flippingAmericaRehab',
      },
      {
        type: 'number',
        name: 'rehabContingencyTurnkey',
        value: assumptionsInput.rehabContingencyTurnkey,
        label: 'Rehab Contingency',
        xs: 12,
        lg: 6,
        tooltipField:
          'Percentage of calculated rehab cost added as a saftey margin',
        assumptions: true,
      },
    ],
    (field) => !field.hide,
  )

  const rehabChartOptions = createDonutChartOptions(
    currencyFormat,
    concat(
      assumptionsInput.rehabTurnkeyType === 'detailedRehab'
        ? isEmpty(analysis.detailedRehabCostTurnkey)
          ? ['Total Rehab']
          : map(
              analysis.detailedRehabCostTurnkey,
              (line) => line.type || 'Unlabeled',
            )
        : ['Total Rehab'],
      [],
    ),
    pieColors,
  )
  const rehabChartSeries = concat(
    assumptionsInput.rehabTurnkeyType === 'detailedRehab'
      ? isEmpty(analysis.detailedRehabCostTurnkey)
        ? [0.0001]
        : map(analysis.detailedRehabCostTurnkey, (line, index) =>
            index === 0
              ? round(line.value, 2) || 0.0001
              : round(line.value, 2) || 0,
          )
      : [round(analytics.turnkey.corTotalTurnkey, 2) || 0.0001],
    [],
  )

  const financingFields = [
    {
      type: 'checkbox',
      label: 'Cash Purchase',
      name: 'allCashPurchaseTurnkey',
      value: assumptionsInput.allCashPurchaseTurnkey,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Investor pays 100% of the purchase price in cash and does not have a mortgage payment.',
      dataTour: 'financing-step-1',
    },
    {
      type: 'percentage',
      name: 'downPaymentTurnkey',
      label: 'Down Payment',
      value: assumptionsInput.downPaymentTurnkey || 0,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'The amount of cash that a purchaser must put down, toward the purchase price, to buy real estate. The down payment amount is a percentage of the total purchase price, dictated by the lender.',
      dataTour: 'financing-step-2',
    },
    {
      type: 'percentage',
      name: 'financingRepairsPercentageTurnkey',
      label: '% of Rehab',
      value: analysisInput.financingRepairsPercentageTurnkey || 0,
      xs: 12,
      lg: 6,
      tooltipField:
        'The percentage of the repair cost that is being covered by the loan',
      dataTour: 'financing-step-7',
    },
    {
      type: 'percentage',
      name: 'rateTurnkey',
      label: 'Interest Rate',
      value: assumptionsInput.rateTurnkey || 0,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'The interest rate is the amount a lender charges for the use of capital, expressed as a percentage of the principal. The interest rate is typically noted on an annual basis known as the annual percentage rate (APR).',
      dataTour: 'financing-step-3',
    },
    {
      type: 'dropdown',
      name: 'termTurnkey',
      label: 'Financing Term',
      value: assumptionsInput.termTurnkey || 0,
      adornment: assumptionsInput.termTurnkeyType,
      adornmentToggle: assumptionsInput.termTurnkeyType,
      adornmentName: 'termTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'The length of time that the loan is being amortized. Expressed in months or years.',
      dataTour: 'financing-step-4',
    },
    {
      type: 'number',
      name: 'pointsTurnkey',
      label: 'Points',
      value: assumptionsInput.pointsTurnkey || 0,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Often when you take out a loan, you will pay "points" (aka: fees) on the loan. It is essentially prepaid interest charged by the lender. One point is equal to 1% of the loan amount. For example, a $100,000 loan with 2 points would be $2,000.',
      dataTour: 'financing-step-5',
    },
    {
      type: 'checkbox',
      name: 'interestOnlyLoanTurnkey',
      label: 'Interest Only Loan',
      value: assumptionsInput.interestOnlyLoanTurnkey,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'A loan that only requires the payment of interest, not principal.',
      dataTour: 'financing-step-6',
    },
    // {
    //   type: 'checkbox',
    //   name: 'financingRepairsTurnkey',
    //   label: 'Finance Repairs',
    //   value: analysisInput.financingRepairsTurnkey || false,
    //   xs: 12,
    //   lg: 6,
    //   tooltipField: 'Your down payment adjusts what percentage of your repairs will be financed.',
    //   dataTour: "financing-step-7"
    // },
  ]

  const projectionCard =
    find(
      cards,
      (card) =>
        card.name === 'projection' &&
        card.member === activeOrg.member,
    ) ||
    find(
      cards,
      (card) => card.name === 'projection' && card.member === null,
    ) ||
    {}
  const projectionAssumptionsFields = map(
    get(projectionCard, 'card.fields'),
    (field) => {
      const input = field.analytics
        ? analytics.turnkey
        : field.assumptions
          ? assumptionsInput
          : analysisInput
      const name = field.suffix
        ? includes(field.name, 'Type')
          ? replace(field.name, 'Type', 'TurnkeyType')
          : field.name + 'Turnkey'
        : field.name
      const adornmentName =
        field.suffix && field.adornmentName
          ? includes(field.adornmentName, 'Type')
            ? replace(field.adornmentName, 'Type', 'TurnkeyType')
            : field.adornmentName + 'Turnkey'
          : field.adornmentName
      const definition =
        field.suffix && field.definition
          ? includes(field.definition, 'Type')
            ? replace(field.definition, 'Type', 'TurnkeyType')
            : field.definition + 'Turnkey'
          : field.definition
      return {
        ...field,
        name,
        value: get(input, name),
        adornmentToggle: get(input, adornmentName),
        adornment: get(input, adornmentName),
        adornmentName: adornmentName,
        definition: definition,
      }
    },
  )

  const chartOptions = createCashflowChartOptions(currencyFormat)
  const chartSeries = [
    {
      data: [
        round(sum(grossMonthlyIncomeChartSeries)) || 0,
        round(sum(monthlyExpensesChartSeries)) || 0,
        round(analytics?.turnkey?.cashFlowTurnkey / 12) || 0,
      ],
      // colors: [
      //   ranges: [
      //     {
      //       from: 100,
      //       to: 0,
      //       color: '#f83145',
      //     },
      //   ],
      // ],
    },
  ]

  const strategySummary = createStrategySummary(
    analytics,
    'turnkey',
    'Turnkey',
    [
      {
        value: analytics.turnkey.cashFlowTurnkey / 12 || '-',
        arrow: analytics.turnkey.cashFlowTurnkeyArrow || 0,
        label: 'Net Cashflow',
        type: 'currency',
        target: assumptions.belowCashFlowTurnkey,
        targetLabel: 'Min Net Cashflow Target',
        targetName: 'belowCashFlowTurnkey',
        tourStep: 'targets-step-1',
        min: 0,
        max: 2000,
        minLabel: '$ 0k',
        maxLabel: '$ 2k',
        step: 100,
        valueLabelFormat: (x) =>
          x >= 1000 ? `${x / 1000}k` : `${x}`,
        description:
          'Net cashflow is the amount of money left after the mortgage and any other expenses are paid.',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Annual Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(grossMonthlyIncomeChartSeries) * 12),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Property Mgmt
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyManagementDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Maintenance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.maintenanceTurnkeyDollar,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Insurance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyInsuranceDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Taxes
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analysis.propertyTaxes, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -HOA Fees
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.turnkey.hoaTotal, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Asset management
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .assetManagementFeeTotalTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Utilities
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.utilitiesTypeDollarTurnkey *
                        12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Capex
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .monthlyReservesTypeDollarTurnkey * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Other
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(assumptions.otherExpenseTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Custom Expenses
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.customMonthlyExpenseTotal * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Operating Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.netOperatingIncomeTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Annual Mortgage
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.turnkey.mortgageTurnkey * 12, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Annual Cashflow
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(analytics.turnkey.cashFlowTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  12 months
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  12
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Monthly Cashflow
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(analytics.turnkey.cashFlowTurnkey / 12, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        ),
      },
      {
        value: analytics.turnkey.cashOnCashReturnTurnkey || '-',
        arrow: analytics.turnkey.cashOnCashReturnTurnkeyArrow || 0,
        label: 'CoC ROI',
        target: assumptions.belowSellingCashOnCashReturn,
        targetLabel: 'Min Cash on Cash Return Target',
        targetName: 'belowSellingCashOnCashReturn',
        tourStep: 'targets-step-2',
        min: 0,
        max: 25,
        minLabel: '0%',
        maxLabel: '25%',
        description:
          'A cash-on-cash return calculates the return on the money put in by the investor on a property with a mortgage.  Commonly referred to as a Leveraged Return.',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Annual Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(grossMonthlyIncomeChartSeries) * 12),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Property Mgmt
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyManagementDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Maintenance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.maintenanceTurnkeyDollar,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Insurance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyInsuranceDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Taxes
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analysis.propertyTaxes, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -HOA Fees
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.turnkey.hoaTotal, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Asset management
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .assetManagementFeeTotalTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Utilities
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.utilitiesTypeDollarTurnkey *
                        12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Capex
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .monthlyReservesTypeDollarTurnkey * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Other
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(assumptions.otherExpenseTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Custom Expenses
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.customMonthlyExpenseTotal * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Operating Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.netOperatingIncomeTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Annual Mortgage
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.turnkey.mortgageTurnkey * 12, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Annual Cashflow
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(analytics.turnkey.cashFlowTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  Total Out-of-Pocket
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.totalOutOfPocketTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Cash on Cash Return
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {analytics.turnkey.cashOnCashReturnTurnkey > 1000
                    ? '+1000%'
                    : percentageFormat.format(
                        analytics.turnkey.cashOnCashReturnTurnkey,
                      )}
                </Typography>
              </Grid>
            </Grid>
          </>
        ),
      },
      {
        value: analytics.turnkey.capRateTurnkey,
        arrow: analytics.turnkey.capRateTurnkeyArrow || 0,
        label: 'Cap Rate',
        target: assumptions.belowSellingCapRate,
        targetLabel: 'Min Cap Rate Target',
        targetName: 'belowSellingCapRate',
        tourStep: 'targets-step-3',
        min: 0,
        max: 25,
        minLabel: '0%',
        maxLabel: '25%',
        description:
          'The capitalization rate (cap rate) is utilized for several reasons inside real estate investing including real estate valuation, comparing properties to each other, analyzing a market, etc. The higher the cap rate the higher your returns/risk.',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Annual Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(grossMonthlyIncomeChartSeries) * 12),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Property Mgmt
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyManagementDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Maintenance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.maintenanceTurnkeyDollar,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Insurance
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .propertyInsuranceDollarTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Taxes
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analysis.propertyTaxes, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -HOA Fees
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.turnkey.hoaTotal, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Asset management
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .assetManagementFeeTotalTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Utilities
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.utilitiesTypeDollarTurnkey *
                        12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Capex
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .monthlyReservesTypeDollarTurnkey * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Other
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(assumptions.otherExpenseTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Custom Expenses
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analytics.customMonthlyExpenseTotal * 12,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Operating Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.netOperatingIncomeTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  All-in-cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  {currencyFormat.format(
                    round(analytics.turnkey.allInCostTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Cap Rate
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {analytics.turnkey.capRateTurnkey > 1000
                    ? '+1000%'
                    : percentageFormat.format(
                        analytics.turnkey.capRateTurnkey,
                      )}
                </Typography>
              </Grid>
            </Grid>
          </>
        ),
      },
      {
        value: analytics.turnkey.IRRTurnkey || '-',
        arrow: analytics.turnkey.IRRTurnkeyArrow || 0,
        label: 'IRR',
        target: assumptions.belowIRRTurnkey,
        targetLabel: 'Min IRR Target',
        targetName: 'belowIRRTurnkey',
        tourStep: 'targets-step-4',
        min: 0,
        max: 30,
        minLabel: '0%',
        maxLabel: '30%',
        description:
          'The internal rate of return (IRR) measures the rate of return earned on an investment during a specific time frame. It includes the recurring cash flow (rent, etc.) and any profits made from the property’s sale. Simply put, IRR represents a property’s net cash flow and expected appreciation divided by the hold time.\nWhile IRR is a helpful way to estimate your asset’s performance over the entire time that you plan to hold it, it relies heavily on forecasting years of cash flow and a projected sale price.',
        extraContent: (
          <Grid size={12} sx={strategyMarginBottom10Sx}>
            <Box sx={{
              "& > .MuiPaper-root": {
                overflow: "hidden",
                boxShadow: "none !important",
                borderRadius: 0,
                "&:first-of-type": { borderTopLeftRadius: "0.65rem", borderTopRightRadius: "0.65rem" },
                "&:last-of-type": { borderBottomLeftRadius: "0.65rem", borderBottomRightRadius: "0.65rem" },
                "&:not(:last-of-type)": { borderBottom: 0 },
                "& > .MuiPaper-root": { boxShadow: "none !important", borderRadius: "inherit" },
              },
              "& .panel-title, & .accordion-toggle, & [data-hf=\"panel-title\"], & [data-hf=\"accordion-toggle\"]": { width: "100%", display: "block" },
              "& .MuiCard-root > div:has(> .panel-title), & .MuiCard-root > div:has(> [data-hf=\"panel-title\"])": {
                display: "block !important",
                padding: "0 !important",
                width: "100%",
                backgroundColor: "transparent !important",
              },
              "& > .card": {
                overflow: "hidden",
                "&:not(:last-of-type)": { borderBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                "&:not(:first-of-type)": { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
                "& > .card-header": { borderRadius: 0, marginBottom: 0 },
              },
            }}>
              <Card variant="outlined" sx={(accordion === 0) && {
                "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                  borderBottomColor: "rgba(79, 80, 98, 0.3)",
                  background: "#fff",
                  color: "#070919 !important",
                  "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                },
                "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
              }}>
                <Card>
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      paddingLeft: '1.25rem',
                      paddingRight: '1.25rem',
                      marginBottom: '0px',
                      backgroundColor: '#fff',
                    }}>
                    <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                      <Box data-hf="accordion-toggle" sx={{
                        "& .MuiButton-root": {
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          textAlign: "left",
                          fontWeight: 700,
                          color: "#3b3e66 !important",
                          textDecoration: "none !important",
                          textTransform: "none",
                          borderRadius: 0,
                          padding: "0.75rem 1.25rem !important",
                          borderBottom: "1px solid transparent",
                          background: "#f4f5fd",
                          "&:hover": {
                            background: "#f8f9ff !important",
                            color: "#070919 !important",
                            textDecoration: "none !important",
                            "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                          },
                          "&:not(:disabled):hover": { transform: "translateY(0px)" },
                          "& [data-hf=\"accordion-icon\"]": {
                            transform: "rotate(90deg)",
                            transition: "all 0.2s ease-in-out",
                            marginLeft: "auto",
                            color: "#368cff !important",
                            overflow: "visible",
                          },
                        },
                        "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                          background: "#2a2a2a !important",
                          color: "rgba(255, 255, 255, 0.9) !important",
                          "&:hover": { background: "#333 !important", color: "#fff !important" },
                        },
                      }}>
                        <Button
                          variant="text"
                          size="large"
                          sx={{
                            fontWeight: 700,
                            color: "#3b3e66",
                            textDecoration: "none",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#4191ff",
                              textDecoration: "underline",
                              transform: "translateY(0px)",
                            },
                            "&:active": { textDecoration: "underline", boxShadow: "none" },
                            "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                            "& span": {
                              position: "relative",
                              "&::before": {
                                content: "\"\"",
                                position: "absolute",
                                width: "100%",
                                height: 2,
                                bottom: 0,
                                left: 0,
                                visibility: "hidden",
                                transform: "scaleX(0)",
                                transition: "all 0.3s ease-in-out 0s",
                              },
                              transition: "all 0.2s ease-in-out",
                            },
                            "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                            "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={() => toggleAccordion(0)}
                          aria-expanded={accordion === 0}
                        >
                          <span>Start Month</span>
                          <FontAwesomeIcon icon={['fas', 'angle-up']} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={accordion === 0}>
                    <Paper sx={strategyFullWidthHiddenSx}>
                      <TableContainer sx={strategyMaxHeight440Sx}>
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead>
                            <TableRow>
                              {IRRTableFirstHeaders.map((row) => (
                                <TableCell key={row.name} align={row.align}>
                                  {row.name}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow
                              key={IRRDataFirst.name} sx={strategyTableRowLastSx}>
                              <TableCell component="th" scope="row">
                                {IRRDataFirst.monthYear}
                              </TableCell>
                              <TableCell align="center">
                                {currencyFormat2.format(
                                  IRRDataFirst.totalOutOfPocket,
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Collapse>
                </Card>
              </Card>
              <Card variant="outlined" sx={(accordion === 1) && {
                "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                  borderBottomColor: "rgba(79, 80, 98, 0.3)",
                  background: "#fff",
                  color: "#070919 !important",
                  "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                },
                "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
              }}>
                <Card>
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      paddingLeft: '1.25rem',
                      paddingRight: '1.25rem',
                      marginBottom: '0px',
                      backgroundColor: '#fff',
                    }}>
                    <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                      <Box data-hf="accordion-toggle" sx={{
                        "& .MuiButton-root": {
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          textAlign: "left",
                          fontWeight: 700,
                          color: "#3b3e66 !important",
                          textDecoration: "none !important",
                          textTransform: "none",
                          borderRadius: 0,
                          padding: "0.75rem 1.25rem !important",
                          borderBottom: "1px solid transparent",
                          background: "#f4f5fd",
                          "&:hover": {
                            background: "#f8f9ff !important",
                            color: "#070919 !important",
                            textDecoration: "none !important",
                            "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                          },
                          "&:not(:disabled):hover": { transform: "translateY(0px)" },
                          "& [data-hf=\"accordion-icon\"]": {
                            transform: "rotate(90deg)",
                            transition: "all 0.2s ease-in-out",
                            marginLeft: "auto",
                            color: "#368cff !important",
                            overflow: "visible",
                          },
                        },
                        "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                          background: "#2a2a2a !important",
                          color: "rgba(255, 255, 255, 0.9) !important",
                          "&:hover": { background: "#333 !important", color: "#fff !important" },
                        },
                      }}>
                        <Button
                          variant="text"
                          size="large"
                          sx={{
                            fontWeight: 700,
                            color: "#3b3e66",
                            textDecoration: "none",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#4191ff",
                              textDecoration: "underline",
                              transform: "translateY(0px)",
                            },
                            "&:active": { textDecoration: "underline", boxShadow: "none" },
                            "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                            "& span": {
                              position: "relative",
                              "&::before": {
                                content: "\"\"",
                                position: "absolute",
                                width: "100%",
                                height: 2,
                                bottom: 0,
                                left: 0,
                                visibility: "hidden",
                                transform: "scaleX(0)",
                                transition: "all 0.3s ease-in-out 0s",
                              },
                              transition: "all 0.2s ease-in-out",
                            },
                            "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                            "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={() => toggleAccordion(1)}
                          aria-expanded={accordion === 1}
                        >
                          <span>Appreciation Months</span>
                          <FontAwesomeIcon icon={['fas', 'angle-up']} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={accordion === 1}>
                    <Paper sx={strategyFullWidthHiddenSx}>
                      <TableContainer sx={strategyMaxHeight440Sx}>
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead>
                            <TableRow>
                              {IRRTableHeaders.map((row) => (
                                <TableCell key={row.name} align={row.align}>
                                  {row.name}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {IRRData.map((row) => (
                                <TableRow
                                  key={row.monthYear || row.name} sx={strategyTableRowLastSx}>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                  >
                                    {row.monthYear}
                                  </TableCell>
                                  <TableCell align="center">
                                    {currencyFormat2.format(
                                      row.income,
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {currencyFormat2.format(
                                      row.expenses,
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {currencyFormat2.format(
                                      row.mortgage,
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {currencyFormat2.format(row.flow)}
                                  </TableCell>
                                </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Collapse>
                </Card>
              </Card>
              <Card variant="outlined" sx={(accordion === 2) && {
                "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                  borderBottomColor: "rgba(79, 80, 98, 0.3)",
                  background: "#fff",
                  color: "#070919 !important",
                  "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                },
                "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
              }}>
                <Card>
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      paddingLeft: '1.25rem',
                      paddingRight: '1.25rem',
                      marginBottom: '0px',
                      backgroundColor: '#fff',
                    }}>
                    <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                      <Box data-hf="accordion-toggle" sx={{
                        "& .MuiButton-root": {
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          textAlign: "left",
                          fontWeight: 700,
                          color: "#3b3e66 !important",
                          textDecoration: "none !important",
                          textTransform: "none",
                          borderRadius: 0,
                          padding: "0.75rem 1.25rem !important",
                          borderBottom: "1px solid transparent",
                          background: "#f4f5fd",
                          "&:hover": {
                            background: "#f8f9ff !important",
                            color: "#070919 !important",
                            textDecoration: "none !important",
                            "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                          },
                          "&:not(:disabled):hover": { transform: "translateY(0px)" },
                          "& [data-hf=\"accordion-icon\"]": {
                            transform: "rotate(90deg)",
                            transition: "all 0.2s ease-in-out",
                            marginLeft: "auto",
                            color: "#368cff !important",
                            overflow: "visible",
                          },
                        },
                        "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                          background: "#2a2a2a !important",
                          color: "rgba(255, 255, 255, 0.9) !important",
                          "&:hover": { background: "#333 !important", color: "#fff !important" },
                        },
                      }}>
                        <Button
                          variant="text"
                          size="large"
                          sx={{
                            fontWeight: 700,
                            color: "#3b3e66",
                            textDecoration: "none",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#4191ff",
                              textDecoration: "underline",
                              transform: "translateY(0px)",
                            },
                            "&:active": { textDecoration: "underline", boxShadow: "none" },
                            "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                            "& span": {
                              position: "relative",
                              "&::before": {
                                content: "\"\"",
                                position: "absolute",
                                width: "100%",
                                height: 2,
                                bottom: 0,
                                left: 0,
                                visibility: "hidden",
                                transform: "scaleX(0)",
                                transition: "all 0.3s ease-in-out 0s",
                              },
                              transition: "all 0.2s ease-in-out",
                            },
                            "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                            "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={() => toggleAccordion(2)}
                          aria-expanded={accordion === 2}
                        >
                          <span>Final Month</span>
                          <FontAwesomeIcon icon={['fas', 'angle-up']} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={accordion === 2}>
                    <Paper sx={strategyFullWidthHiddenSx}>
                      <TableContainer sx={strategyMaxHeight440Sx}>
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead>
                            <TableRow>
                              {IRRTableLastHeaders.map((row) => (
                                <TableCell key={row.name} align={row.align}>
                                  {row.name}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow
                              key={IRRDataLast.name} sx={strategyTableRowLastSx}>
                              <TableCell component="th" scope="row">
                                {IRRDataLast.monthYear}
                              </TableCell>
                              <TableCell align="center">
                                {currencyFormat2.format(
                                  IRRDataLast.lastMonthCashFlow,
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Collapse>
                </Card>
              </Card>
            </Box>
          </Grid>
        ),
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategySectionLabelSx}>
                  Due to the complexity of this calculation, we only
                  show an overview of what goes into calculating the
                  IRR
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Initial Investment (Out of pocket)
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.totalOutOfPocketTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Monthly Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(grossMonthlyIncomeChartSeries)),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Rent Growth Rate
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.growthRateIncome > 1000
                    ? '+1000%'
                    : assumptions.growthRateIncome}
                  %
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Monthly Expenses
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(monthlyExpensesChartSeries)),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Expenses Growth Rate
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.growthRateExpense > 1000
                    ? '+1000%'
                    : assumptions.growthRateExpense}
                  %
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  After Repair Value
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.turnkey.arvTotalTurnkey, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Appreciation Rate
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.homePriceAppreciation > 1000
                    ? '+1000%'
                    : assumptions.homePriceAppreciation}
                  %
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Holding Period (years)
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.rentalHoldingPeriodTurnkey}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Future Property Sale at Holding Period
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey
                        .futureSaleValueAtHoldingPeriodTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyItalicMutedSx}>
                  Future Property Sale at Loan Payoff
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyItalicMutedSx}>
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.futureSaleValueTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  IRR
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {analytics.turnkey.IRRTurnkey > 1000
                    ? '+1000%'
                    : percentageFormat.format(
                        analytics.turnkey.IRRTurnkey,
                      )}
                </Typography>
              </Grid>
            </Grid>
            {/* <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>-Annual Expenses</Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>-1,308</Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>Net Operating Income</Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>14892</Typography>
              </Grid>
            </Grid>
            <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon icon={['fal', 'divide']} style={strategyIconGapStyle} />All-in-cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon icon={['fal', 'divide']} style={strategyIconGapStyle} />14,892
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>Cap Rate</Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>12.82%</Typography>
              </Grid>
            </Grid> */}
          </>
        ),
      },
    ],
    {
      belowKey: 'belowTargetTurnkey',
      onKey: 'onTargetTurnkey',
      aboveKey: 'aboveTargetTurnkey',
    },
  )

  const financials = [
    {
      value: analytics.turnkey.allInCostTurnkey,
      label: 'All-in Cost',
      description:
        'Total amount of initial investment into a property, including renovation and points paid for financing.',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Purchase Price
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(
                  round(
                    analysis.turnkeyOfferPrice ||
                      analysis.resellPriceTurnkey ||
                      analytics.turnkey.arvTotalTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Acquisition Commission
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(
                    analytics.acquisitionCommissionDollarTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Acquisition Fee
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(assumptions.acquisitionFee, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Closing Costs
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.turnkey.closeCostDollarTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Loan Points
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.turnkey.pointsAmountTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Rehab Amount
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.turnkey.corTotalTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Financing Fee
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(
                    analytics.turnkey.financingFeeTotalTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Owner Reserves
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(assumptions.ownerReservesTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Custom Acquisition Expenses
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.customAcquisitionExpenseTotal, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                All-in Cost
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  round(analytics.turnkey.allInCostTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      value: analytics.turnkey.totalOutOfPocketTurnkey,
      label: 'Total Out-of-Pocket',
      description:
        'Total amount of cash that a buyer has put into a property of his/her own capital.',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Down Payment
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(
                  round(analytics.turnkey.downPaymentTotalTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Acquisition Commission
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(
                    analytics.acquisitionCommissionDollarTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Acquisition Fee
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(assumptions.acquisitionFee, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Closing Costs
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.turnkey.closeCostDollarTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Financing Fee
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(
                    analytics.turnkey.financingFeeTotalTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Loan Points
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.turnkey.pointsAmountTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Custom Acquisition Expenses
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  round(analytics.customAcquisitionExpenseTotal, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Credit From Seller
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(assumptions.creditFromSeller, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          {!analysis.financingRepairsTurnkey && (
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +Rehab Amount
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +
                  {currencyFormat.format(
                    round(
                      analytics.turnkey.repairsOutOfPocketTurnkey,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Total Out-of-Pocket
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  round(analytics.turnkey.totalOutOfPocketTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
  ]

  const dispositionFields = [
    {
      type: 'dollar',
      name: 'resellPriceTurnkey',
      label: 'Resale Price',
      value:
        analysisInput.resellPriceTurnkey === null
          ? ''
          : analysisInput.resellPriceTurnkey.toString(),
      xs: 12,
      lg: 6,
      tooltipField:
        'The price you would sell the property for once the rehab is completed.',
      dataTour: 'dispositionExpenses-step-2',
    },
    {
      type: 'dropdown',
      name: 'sellerClosingCostTurnkey',
      label: 'Seller Closing Cost',
      value: assumptionsInput.sellerClosingCostTurnkey,
      adornmentToggle: assumptionsInput.sellerClosingCostTurnkeyType,
      adornment: assumptionsInput.sellerClosingCostTurnkeyType,
      adornmentName: 'sellerClosingCostTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Closing Costs Paid by the Seller. Expressed as a dollar amount or percentage of the resale price',
      dataTour: 'dispositionExpenses-step-3',
    },
    {
      type: 'dropdown',
      name: 'salesCommissionTurnkey',
      label: 'Sales Commission',
      value: assumptionsInput.salesCommissionTurnkey,
      adornmentToggle: assumptionsInput.salesCommissionTurnkeyType,
      adornment: assumptionsInput.salesCommissionTurnkeyType,
      adornmentName: 'salesCommissionTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Sales Commission on the Future Sale of Property. Expressed as a dollar amount or percentage of resale price.',
      dataTour: 'dispositionExpenses-step-4',
    },
    {
      type: 'dropdown',
      name: 'dispositionFeeTurnkey',
      label: 'Disposition Fee',
      value: assumptionsInput.dispositionFeeTurnkey,
      adornmentToggle: assumptionsInput.dispositionFeeTurnkeyType,
      adornment: assumptionsInput.dispositionFeeTurnkeyType,
      adornmentName: 'dispositionFeeTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Advanced Fee - Additional marketing fee on top of sales commission. Expressed as a dollar amount or percentage of resale price.',
      dataTour: 'dispositionExpenses-step-5',
    },
    {
      type: 'percentage',
      name: 'homePriceAppreciation',
      label: 'Home Price Appreciation',
      value: assumptionsInput.homePriceAppreciation,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'The % of Appreciation in Total Dollar Value that the Home has gained or lost each year. Expressed as a percentage of ARV (after repair value). If your holding period is less than 12 months, this field will have no effect.',
      dataTour: 'dispositionExpenses-step-6',
    },
    {
      type: 'percentage',
      name: 'growthRateExpense',
      label: 'Expense Growth Rate',
      value: assumptionsInput.growthRateExpense,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'The % increase in property expenses per year. Expressed as a percentage of gross rental income. If your holding period is less than 12 months, this field will have no effect.',
      dataTour: 'dispositionExpenses-step-7',
    },
    {
      type: 'dollar',
      name: 'loanBalanceAtHoldingPeriodTurnkey',
      label: 'Loan Balance at Resale',
      value:
        analytics.turnkey.loanBalanceAtHoldingPeriodTurnkey === null
          ? ''
          : analytics.turnkey.loanBalanceAtHoldingPeriodTurnkey,
      xs: 12,
      lg: 6,
      disabled: true,
      tooltipField:
        'This field is not editable. The remaining loan amount at the end of your flip holding period.',
      dataTour: 'dispositionExpenses-step-8',
    },
  ]

  const calculationDialogs = [
    {
      label: 'Cash Needed to Close',
      description: 'Cash needed to buy the property',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Offer Price
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(analysis.turnkeyOfferPrice)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Closing Cost
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  analytics.turnkey.closeCostDollarTurnkey,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Cash Needed to Close
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  analytics.turnkey.closeCostDollarTurnkey +
                    analysis.turnkeyOfferPrice,
                )}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'Total Cash Needed',
      description:
        'Cash needed to be spent on the property before it can be sold',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Offer Price
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(analysis.turnkeyOfferPrice)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Closing Cost
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  analytics.turnkey.closeCostDollarTurnkey,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Cash Needed to Close
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  analytics.turnkey.closeCostDollarTurnkey +
                    analysis.turnkeyOfferPrice,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +Rehab Cost
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                +
                {currencyFormat.format(
                  analytics.turnkey.corTotalTurnkey,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Total Cash Needed
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  analytics.turnkey.closeCostDollarTurnkey +
                    analysis.turnkeyOfferPrice +
                    analytics.turnkey.corTotalTurnkey,
                )}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      label: 'NOI',
      description: 'Net Operating Income',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Gross Annual Income
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(
                  round(sum(grossMonthlyIncomeChartSeries) * 12),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Property Mgmt
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey.propertyManagementDollarTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Maintenance
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey.maintenanceTurnkeyDollar,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Insurance
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey.propertyInsuranceDollarTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Taxes
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(analysis.propertyTaxes, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -HOA Fees
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(analytics.turnkey.hoaTotal, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Asset management
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey.assetManagementFeeTotalTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Utilities
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey.utilitiesTypeDollarTurnkey * 12,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Capex
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(
                    analytics.turnkey
                      .monthlyReservesTypeDollarTurnkey * 12,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Other
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(assumptions.otherExpenseTurnkey, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Custom Expenses
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  round(analytics.customMonthlyExpenseTotal * 12, 0),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Net Operating Income
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(
                  round(
                    analytics.turnkey.netOperatingIncomeTurnkey,
                    0,
                  ),
                )}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
    // {
    //   label: 'Cap Rate',
    //   description:
    //     'Cap Rate is calculated by dividing the Net Operating Income(NOI) by the offer price',
    //   calculation: (
    //     <>
    //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             Gross Annual Income
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             {currencyFormat.format(
      //               round(sum(grossMonthlyIncomeChartSeries) * 12),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Property Mgmt
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.propertyManagementDollarTurnkey,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Maintenance
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.maintenanceTurnkeyDollar,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Insurance
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.propertyInsuranceDollarTurnkey,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Taxes
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(analysis.propertyTaxes, 0),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -HOA Fees
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(analytics.turnkey.hoaTotal, 0),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Asset management
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.assetManagementFeeTotalTurnkey,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Utilities
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.utilitiesTypeDollarTurnkey * 12,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Capex
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey
      //                   .monthlyReservesTypeDollarTurnkey * 12,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Other
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(assumptions.otherExpenseTurnkey, 0),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -Custom Expenses
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             -
      //             {currencyFormat.format(
      //               round(analytics.customMonthlyExpenseTotal * 12, 0),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Divider sx={strategyMutedDividerSx} />
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyWhiteTextSx}>
      //             Net Operating Income
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyWhiteTextSx}>
      //             {currencyFormat.format(
      //               round(
      //                 analytics.turnkey.netOperatingIncomeTurnkey,
      //                 0,
      //               ),
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             <FontAwesomeIcon
      //               icon={['fal', 'divide']}
      //               style={strategyIconGapStyle}
      //             />
      //             Offer Price
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyMutedTextSx}>
      //             <FontAwesomeIcon
      //               icon={['fal', 'divide']}
      //               style={strategyIconGapStyle}
      //             />
      //             {currencyFormat.format(
      //               analysis.turnkeyOfferPrice
      //                 ? analysis.turnkeyOfferPrice
      //                 : 0,
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //       <Divider sx={strategyMutedDividerSx} />
      //       <Grid
      //         container
      //         alignItems="center"
      //         justifyContent="space-between"
      //         sx={strategyMetricRowSx}
      //       >
      //         <Grid>
      //           <Typography sx={strategyWhiteTextSx}>
      //             Cap Rate
      //           </Typography>
      //         </Grid>
      //         <Grid>
      //           <Typography sx={strategyWhiteTextSx}>
      //             {percentageFormat.format(
      //               analysis.turnkeyOfferPrice
      //                 ? analytics.turnkey.netOperatingIncomeTurnkey /
      //                     analysis.turnkeyOfferPrice
      //                 : 0,
      //             )}
      //           </Typography>
      //         </Grid>
      //       </Grid>
      //     </>
      //   ),
      // },
      {
      label: 'Cash on Cash Return',
      description: 'Highest Recommended Offer Price',
      calculation: (
      <>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      Property Mgmt
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      round(
      analytics.turnkey.propertyManagementDollarTurnkey,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Maintenance
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(
      analytics.turnkey.maintenanceTurnkeyDollar,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Insurance
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(
      analytics.turnkey.propertyInsuranceDollarTurnkey,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Taxes
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(analysis.propertyTaxes, 0),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +HOA Fees
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(analytics.turnkey.hoaTotal, 0),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Asset management
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(
      analytics.turnkey.assetManagementFeeTotalTurnkey,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Utilities
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(
      analytics.turnkey.utilitiesTypeDollarTurnkey * 12,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Capex
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(
      analytics.turnkey
      .monthlyReservesTypeDollarTurnkey * 12,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Other
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(assumptions.otherExpenseTurnkey, 0),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +Custom Expenses
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      +
      {currencyFormat.format(
      round(analytics.customMonthlyExpenseTotal * 12, 0),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Divider sx={strategyMutedDividerSx} />
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyWhiteTextSx}>
      Cash Flow Before Taxes
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyWhiteTextSx}>
      {currencyFormat.format(
      round(
      round(sum(grossMonthlyIncomeChartSeries) * 12) -
      analytics.turnkey.netOperatingIncomeTurnkey,
      0,
      ),
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      <FontAwesomeIcon
      icon={['fal', 'divide']}
      style={strategyIconGapStyle}
      />
      Total Cash Needed
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      <FontAwesomeIcon
      icon={['fal', 'divide']}
      style={strategyIconGapStyle}
      />
      {currencyFormat.format(
      analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Divider sx={strategyMutedDividerSx} />
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: 'center',
      justifyContent: 'space-between',
      }]}
      >
      <Grid>
      <Typography sx={strategyWhiteTextSx}>
      Cash On Cash
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyWhiteTextSx}>
      {percentageFormat.format(
      analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey !==
      0
      ? round(sum(grossMonthlyIncomeChartSeries) * 12) -
      analytics.turnkey.netOperatingIncomeTurnkey /
      (analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey)
      : 0,
      )}
      </Typography>
      </Grid>
      </Grid>
      </>
      ),
      },
      ]
      const pepBigPictureFields = [
      {
      sectionName: 'Acquisition',
      fields: filter(
      [
      {
      type: 'dollar',
      name: 'resellPriceTurnkey',
      label: 'List Price',
      value:
      analysisInput.resellPriceTurnkey === null
      ? ''
      : analysisInput.resellPriceTurnkey.toString(),
      // helper:
      //   !analysisInput.resellPriceTurnkey ||
      //   analysisInput.resellPriceTurnkey === '0'
      //     ? '0% Discount to ARV'
      //     : (
      //         (1 -
      //           analysisInput.resellPriceTurnkey /
      //             analytics.turnkey.arvTotalTurnkey) *
      //         100
      //       ).toFixed(0) + '% Discount to ARV',
      xs: 12,
      lg: 6,
      tooltipField:
      'The price that you, the investor, will ask for when you are ready to sell the property.',
      adornmentNumber: 1,
      },
      {
      type: 'dollar',
      name: 'turnkeyOfferPrice',
      label: 'Offer Price',
      value:
      analysisInput.turnkeyOfferPrice == null
      ? ''
      : analysisInput.turnkeyOfferPrice.toString(),
      // helper:
      //   !analysisInput.turnkeyOfferPrice ||
      //   analysisInput.turnkeyOfferPrice === '0'
      //     ? '0% Discount to Price'
      //     : (
      //         (1 -
      //           analysisInput.turnkeyOfferPrice /
      //             analysisInput.listPrice) *
      //         100
      //       ).toFixed(0) + '% Discount to Price',
      xs: 12,
      lg: 6,
      tooltipField:
      'The price that the end buyer will pay for the home.',
      adornmentNumber: 2,
      },
      {
      type: 'dropdown',
      name: 'closingCostTurnkey',
      value: assumptionsInput.closingCostTurnkey,
      label: 'Purchase Closing Costs',
      adornmentToggle: assumptionsInput.closingCostTurnkeyType,
      adornment: assumptionsInput.closingCostTurnkeyType,
      adornmentName: 'closingCostTurnkeyType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
      'Fees (attorney/title fees, etc) Paid at Closing. Expressed as a dollar amount or precentage of the purchase prices.',
      adornmentNumber: 3,
      },
      ],
      (field) => !field.hide,
      ),
      },
      {
      sectionName: 'Income',
      fields: filter(
      [
      {
      type: 'select',
      name: 'rentType',
      label: 'Rent Type',
      value: assumptionsInput.rentType,
      options: assumptionsOptions(activeOrg)?.rentTypes,
      xs: 12,
      lg: 12,
      assumptions: true,
      },
      {
      type: 'dollar',
      name: `rent.${props.activeUnit}`,
      label: 'Total Rental Income',
      tooltipButtonText: 'Show Total Rent data',
      value:
      analysisInput.rent[props.activeUnit] === null
      ? ''
      : analysisInput.rent[props.activeUnit].toString(),
      // helper:
      //   property.sqft &&
      //   property.sqft[props.activeUnit] &&
      //   analysisInput.rent[props.activeUnit]
      //     ? currencyFormat2.format(
      //         analysisInput.rent[props.activeUnit] /
      //           property.sqft[props.activeUnit],
      //       ) + ' rent per sqft'
      //     : '$0.00 rent per sqft',
      xs: 12,
      lg: 6,
      definition: 'rent',
      // disabled: !isNil(analysisInput.ownerOccupiedTurnkey) && props.activeUnit === analysisInput.ownerOccupiedTurnkey,
      tooltipField:
      "A tenant's monthly payment to a landlord for the use of property or land.",
      hide: assumptionsInput.rentType !== 'singleRent',
      adornmentNumber: 4,
      },
      {
      type: 'dollar',
      name: `rentLow.${props.activeUnit}`,
      label: 'Rent Range Low',
      tooltipButtonText: 'Show Total Rent data',
      value:
      analysisInput.rentLow[props.activeUnit] === null
      ? ''
      : analysisInput.rentLow[props.activeUnit].toString(),
      xs: 12,
      lg: 6,
      definition: 'rent',
      tooltipField: 'Lower end of projected rent',
      hide: assumptionsInput.rentType !== 'rentRange',
      dataTour: '',
      adornmentNumber: 4,
      },
      {
      type: 'dollar',
      name: `rentHigh.${props.activeUnit}`,
      label: 'Rent Range High',
      tooltipButtonText: 'Show Total Rent data',
      value:
      analysisInput.rentHigh[props.activeUnit] === null
      ? ''
      : analysisInput.rentHigh[props.activeUnit].toString(),
      xs: 12,
      lg: 6,
      definition: 'rent',
      tooltipField: 'Higher end of projected rent',
      hide: assumptionsInput.rentType !== 'rentRange',
      dataTour: '',
      adornmentNumber: 4,
      },
      {
      type: 'percentage',
      name: `vacancy.${props.activeUnit}`,
      label: 'Vacancy Rate',
      value: assumptionsInput.vacancy[props.activeUnit],
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
      'The percentage of time that a property is not rented out during the course of a year.',
      adornmentNumber: 5,
      },
      ],
      (field) => !field.hide,
      ),
      },
      {
      sectionName: 'Expenses',
      fields: sortBy(
      filter(
      map(monthlyExpensesFields, (field) => {
      let adornmentNumber = 0
      if (includes(field.label, 'Taxes')) {
      adornmentNumber = 6
      } else if (includes(field.label, 'Insurance')) {
      adornmentNumber = 7
      } else if (includes(field.label, 'Mgmt Fee')) {
      adornmentNumber = 8
      } else if (includes(field.label, 'Maintenance')) {
      adornmentNumber = 9
      }
      if (adornmentNumber > 0)
      return {
      ...field,
      adornmentNumber,
      }
      else
      return {
      ...field,
      hide: true,
      }
      }),
      (field) => !field.hide,
      ),
      ['adornmentNumber'],
      ),
      },
      ]
      const dispositionChartOptions = createDonutChartOptions(
      currencyFormat,
      [
      'Seller Closing Cost',
      'Sales Commission',
      'Disposition Fee',
      'Loan Balance at Resale',
      ],
      pieColors,
      )
      const dispositionChartSeries = [
      round(analytics.turnkey.sellingClosingCostDollarTurnkey, 2) ||
      0.0001,
      round(analytics.turnkey.salesCommissionTotalTurnkey, 2) || 0,
      round(analytics.turnkey.dispositionFeeDollarTurnkey, 2) || 0,
      round(analytics.turnkey.loanBalanceAtHoldingPeriodTurnkey, 2) ||
      0,
      ]
      const compData = calculateCompData({
      compValues: props.compValues,
      lowestAverage: props.lowestAverage,
      })
      const turnkeyCards = [
      {
      name: 'bigPicture',
      hide: !activeOrg.isRealeflow,
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Big Picture</b>
      </Box>
      </Box>
      <CardContent>
      {map(pepBigPictureFields, (section) => (
      <>
      <Divider sx={strategyMarginBottom5Sx}/>
      <Typography sx={strategyPad5Sx}>
      {section.sectionName}
      </Typography>
      <Divider sx={strategyMarginBottom5Sx}/>
      <Grid container>
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      >
      {map(section.fields, (section) =>
      renderInputFields(section),
      )}
      </Grid>
      </Grid>
      </Grid>
      </>
      ))}
      <Paper
      elevation={3}
      sx={{ borderRadius: 3, p: 0, background: '#f9fafe' }}
      >
      <Grid container>
      <Grid
      size={{ xs: 12, md: 6 }}
      onClick={() => toggle1('Cash Needed to Close')}
      sx={{
      borderRight: {
      md: '1px solid #e0e0e0',
      xs: 'none',
      },
      borderBottom: '1px solid #e0e0e0',
      p: 2,
      }}
      >
      <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle1" color="textSecondary">
      Cash Needed to Close
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      <CountUp
      start={0}
      end={
      analytics.turnkey.closeCostDollarTurnkey +
      analysisInput.turnkeyOfferPrice
      }
      duration={1}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Typography>
      </Box>
      </Grid>
      <Grid
      size={{ xs: 12, md: 6 }}
      onClick={() => toggle1('Total Cash Needed')}
      sx={{
      borderBottom: '1px solid #e0e0e0',
      p: 2,
      }}
      >
      <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle1" color="textSecondary">
      Total Cash Needed
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      <CountUp
      start={0}
      end={
      analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey
      }
      duration={1}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Typography>
      </Box>
      </Grid>
      <Grid
      size={{ xs: 12, md: 6 }}
      onClick={() => toggle1('NOI')}
      sx={{
      borderRight: {
      md: '1px solid #e0e0e0',
      xs: 'none',
      },
      borderBottom: {
      md: 'none',
      xs: '1px solid #e0e0e0',
      },
      p: 2,
      }}
      >
      <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle1" color="textSecondary">
      NOI
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      <CountUp
      start={0}
      end={analytics.turnkey.netOperatingIncomeTurnkey}
      duration={1}
      separator=","
      decimals={2}
      prefix="$"
      />
      </Typography>
      </Box>
      </Grid>
      <Grid
      size={{ xs: 12, md: 6 }}
      onClick={() => toggle1('Cap Rate')}
      sx={{
      borderBottom: {
      xs: '1px solid #e0e0e0',
      md: 'none',
      },
      p: 2,
      }}
      >
      <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle1" color="textSecondary">
      Cap Rate
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      <CountUp
      start={0}
      end={round(
      analysis.turnkeyOfferPrice
      ? (analytics.turnkey.netOperatingIncomeTurnkey /
      analysis.turnkeyOfferPrice) *
      100
      : 0,
      2,
      )}
      duration={1}
      separator=","
      decimals={2}
      suffix="%"
      />
      </Typography>
      </Box>
      </Grid>
      <Grid
      size={12}
      onClick={() => toggle1('Cash on Cash Return')}
      sx={{
      borderTop: '1px solid #e0e0e0',
      p: 2,
      }}
      >
      <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle1" color="textSecondary">
      Cash on Cash Return
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      <CountUp
      start={0}
      end={
      analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey !==
      0
      ? ((round(
      sum(grossMonthlyIncomeChartSeries) * 12,
      2,
      ) -
      analytics.turnkey.netOperatingIncomeTurnkey) /
      (analytics.turnkey.closeCostDollarTurnkey +
      analysis.turnkeyOfferPrice +
      analytics.turnkey.corTotalTurnkey)) *
      100
      : 0
      }
      duration={1}
      separator=","
      decimals={2}
      suffix="%"
      />
      </Typography>
      </Box>
      </Grid>
      </Grid>
      </Paper>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'strategy',
      card: (
      <StrategyCard
      strategyName="Turnkey"
      activeOrg={activeOrg}
      percentage={strategySummary.percentage}
      onStrategyTourClick={toggleStrategyTour}
      shared={props.shared}
      property={property}
      canDrag={props.canDrag}
      contactAgent={
      !props.shared &&
      activeOrg.member !== 'gorillacapital' ? (
      <Box sx={strategyCardBodyPadSx}>
      <ContactAgent
      property={property}
      propertyPage={true}
      canDrag={props.canDrag}
      />
      </Box>
      ) : null
      }
      />
      )},
      {
      name: 'targets',
      card: (
      <TargetsCard
      strategySummary={strategySummary}
      currencyFormat={currencyFormat}
      percentageFormat={percentageFormat}
      modal1={modal1}
      toggle1={toggle1}
      toggle1Close={toggle1Close}
      renderInputFields={renderInputFields}
      assumptionsInput={assumptionsInput}
      maxOfferPrice={analytics.turnkey.maxTurnkeyPrice}
      toggleMaxOffer={toggleMaxOffer}
      maxOfferDescription={maxOfferDescription}
      onTargetsTourClick={toggleTargetsTour}
      maxOfferCalculation={
      <>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      New Asking Price:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analysisInput.resellPriceTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      End Buyer Purchase Price:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analysisInput.turnkeyOfferPrice,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      ARV:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analytics.turnkey.arvTotalTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      All In Cost:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analytics.turnkey.allInCostTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      Total Out Of Pocket:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analytics.turnkey.totalOutOfPocketTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      Annual Cash Flow:
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {currencyFormat.format(
      analytics.turnkey.cashFlowTurnkey,
      )}
      </Typography>
      </Grid>
      </Grid>
      <Grid
      container
      sx={[strategyMetricRowSx, {
      alignItems: "center",
      justifyContent: "space-between"
      }]}>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      Holding Period (yr):
      </Typography>
      </Grid>
      <Grid>
      <Typography sx={strategyMutedTextSx}>
      {assumptionsInput.rentalHoldingPeriodTurnkey}
      </Typography>
      </Grid>
      </Grid>
      </>
      }
      />
      )},
      {
      name: 'monthlyCashflow',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyTableRowSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <Box component="small" sx={strategyUppercaseMetaSx}>
      Summary
      </Box>
      <b>Monthly Cashflow</b>
      </Box>
      <div>
      <Tooltip title="Shows the income, expenses, and projected overall cashflow of the property">
      <Button
      onClick={() =>
      setOpenTurnkeyMonthlyCashflowTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Grid>
      <Box sx={{
        borderRadius: "0.65rem",
        p: 1.5,
        mb: 1,
        bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
        textAlign: "center",
        border: "1px solid rgba(59, 62, 102, 0.1)",
        "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 1.25,
          py: 0.75,
          gap: 0.25,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
        "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
          minHeight: "2.6em",
          lineHeight: 1.3,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          px: 0.5,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
          color: "#070919",
          fontWeight: 700,
          fontSize: "1.15rem",
          lineHeight: 1.2,
        },
      }}>
      <Grid container sx={{ color: "rgba(59, 62, 102, 0.5)" }}>
      <Grid
      size={{ xs: 4 }}
      data-tour="monthlyCashflow-step-1"
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Income
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box sx={{
      display: 'inline-block',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0.7em',
      paddingRight: '0.7em',
      fontSize: '70%',
      fontWeight: 700,
      lineHeight: '21px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      verticalAlign: 'baseline',
      borderRadius: '100%',
      textTransform: 'uppercase',
      height: 10,
      color: '#fff',
      backgroundColor: '#1bc943',
      textIndent: '-999rem',
      width: 10,
      padding: '0px',
      boxShadow: '0 0 0 2px #fff',
      marginRight: '0.5rem',
      }}>
      fast
      </Box>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      sum(grossMonthlyIncomeChartSeries) /
      1000
      }
      duration={1}
      separator=","
      delay={2}
      decimals={1}
      decimal="."
      prefix="$"
      suffix="k"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      data-tour="monthlyCashflow-step-2"
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Expenses
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box sx={{
      display: 'inline-block',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0.7em',
      paddingRight: '0.7em',
      fontSize: '70%',
      fontWeight: 700,
      lineHeight: '21px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      verticalAlign: 'baseline',
      borderRadius: '100%',
      textTransform: 'uppercase',
      height: 10,
      color: '#fff',
      backgroundColor: '#f83245',
      textIndent: '-999rem',
      width: 10,
      padding: '0px',
      boxShadow: '0 0 0 2px #fff',
      marginRight: '0.5rem',
      }}>
      normal
      </Box>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      sum(monthlyExpensesChartSeries) / 1000
      }
      duration={1}
      delay={2}
      decimals={1}
      decimal="."
      prefix="$"
      suffix="k"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      data-tour="monthlyCashflow-step-3"
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Cashflow
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box sx={{
      display: 'inline-block',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0.7em',
      paddingRight: '0.7em',
      fontSize: '70%',
      fontWeight: 700,
      lineHeight: '21px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      verticalAlign: 'baseline',
      borderRadius: '100%',
      textTransform: 'uppercase',
      height: 10,
      color: '#fff',
      backgroundColor: '#368cff',
      textIndent: '-999rem',
      width: 10,
      padding: '0px',
      boxShadow: '0 0 0 2px #fff',
      marginRight: '0.5rem',
      }}>
      slow
      </Box>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey.cashFlowTurnkey /
      12 /
      1000
      }
      duration={1}
      delay={2}
      decimals={1}
      decimal="."
      prefix="$"
      suffix="k"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      </Grid>
      </Box>
      <>
      <Suspense fallback={<LoadingSkeleton variant="chart" />}>
      <Box
      sx={{
      width: '100%',
      minWidth: 0,
      overflow: 'visible',
      }}
      >
      <Chart
      options={chartOptions}
      series={chartSeries}
      type="bar"
      width="100%"
      height={250}
      />
      </Box>
      </Suspense>
      </>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'acquisition',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Acquisition Expenses</b>
      </Box>
      <div>
      <Tooltip title="The total projected expenses required to purchase the property">
      <Button
      onClick={() =>
      setOpenTurnkeyAcquisitionExpensesTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Grid>
      <Grid>
      <Box sx={strategyFlexCenterOnlySx}>
      <Suspense fallback={<LoadingSkeleton variant="chart" />}>
      <Chart
      options={acquisitionChartOptions}
      series={acquisitionChartSeries}
      type="donut"
      width="300"
      />
      </Suspense>
      </Box>
      </Grid>
      <Divider sx={strategyBlockSpacerSx} />
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      sx={{ alignItems: 'flex-start' }}
      >
      <Grid
      size={12}
      container
      sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      flexWrap: 'nowrap',
      }}
      >
      <Grid data-tour="acquisitionExpenses-step-1">
      <FormGroup row>
      <FormControlLabel
      control={
      <Checkbox
      id="Advanced Fields"
      checked={state.checkedA}
      onChange={handleChange}
      name="checkedA"
      />
      }
      label="Advanced Fields"
      />
      </FormGroup>
      </Grid>
      {(!props.publicDashboard ||
      !property.hideDaysOnMarketplace) && (
      <Grid data-tour="acquisitionExpenses-step-2">
      <Tooltip title="Refers to the number of days a house is listed on the market and the day it is sold.">
      <Typography sx={strategyTableCellRightSx}>
      <FontAwesomeIcon
      icon={['far', 'clock']}
      style={strategyIconGap5Style}
      />
      {analysisInput.daysOnMarket} days on
      market
      </Typography>
      </Tooltip>
      </Grid>
      )}
      </Grid>
      {map(
      state.checkedA
      ? concat(
      basicAcquisitionFields,
      advancedAcquisitionFields,
      customAcquisitonField,
      )
      : concat(
      basicAcquisitionFields,
      customAcquisitonField,
      ),
      (field) => renderInputFields(field),
                  )}
      </Grid>
      </Grid>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'income',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Grid
      container
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#1e1e1e !important", color: "rgba(255, 255, 255, 0.87)" },
      }}
      >
      <Grid size={8} sx={{ minWidth: 0 }}>
      <List sx={{
        display: "flex",
        alignItems: "stretch",
        gap: 0.5,
        p: 0,
        m: 0,
        minHeight: 40,
      }}>
      {analysisInput.rent.length > 1 ? (
      map(analysisInput.rent, (value, index) => (
      <ListItemButton
      key={`unit-${index}`}
      selected={props.activeUnit === index}
      onClick={() => props.setActiveUnit(index)}
      sx={{
        width: "auto",
        minWidth: "auto",
        px: 1.5,
        py: 1,
        minHeight: 40,
        borderRadius: 0,
        color: "#4f5062",
        fontWeight: 500,
        "&.Mui-selected": {
          color: "#368cff",
          fontWeight: 700,
          bgcolor: "transparent",
          borderBottom: "3px solid #368cff",
        },
        "&.Mui-selected:hover": { bgcolor: "transparent", color: "#368cff" },
      }}
      >
      Unit {index + 1}
      </ListItemButton>
      ))
      ) : (
      <ListItem sx={{ my: "0.25rem" }}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Gross Monthly Income</b>
      </Box>
      </ListItem>
      )}
      </List>
      </Grid>
      <Grid style={strategyIconGap20Style}>
      <Tooltip title="Total income generated from the property on a monthly basis">
      <Button
      onClick={() =>
      setOpenTurnkeyGrossMonthlyIncomeTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </Grid>
      </Grid>
      <Divider />
      <CardContent>
      <Grid>
      <Grid>
      <Box sx={strategyFlexCenterOnlySx}>
      <Suspense fallback={<LoadingSkeleton variant="chart" />}>
      <Chart
      options={grossMonthlyIncomeChartOptions}
      series={grossMonthlyIncomeChartSeries}
      type="donut"
      width="300"
      />
      </Suspense>
      </Box>
      </Grid>
      <Divider sx={strategyBlockSpacerSx} />
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      sx={{ alignItems: 'flex-start' }}
      >
      {map(grossMonthlyIncomeFields, (field) =>
      renderInputFields(field),
      )}
      </Grid>
      </Grid>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'expenses',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Monthly Expenses</b>
      </Box>
      <div>
      <Tooltip title="Projected monthly expenditures and applicable costs associated with the property">
      <Button
      onClick={() =>
      setOpenTurnkeyMonthlyExpensesTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Grid>
      <Grid>
      <Box sx={strategyFlexCenterOnlySx}>
      <Suspense fallback={<LoadingSkeleton variant="chart" />}>
      <Chart
      options={monthlyExpensesChartOptions}
      series={monthlyExpensesChartSeries}
      type="donut"
      width="300"
      />
      </Suspense>
      </Box>
      </Grid>
      <Divider sx={strategyBlockSpacerSx} />
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center' }}
      >
      {map(monthlyExpensesFields, (field) =>
      renderInputFields(field),
      )}
      </Grid>
      </Grid>
      <Grid>
      <Grid container direction="row" spacing={1}>
      {!isEmpty(property.propertyManagementGroups) && (
      <Grid size={12}>
      <Button
      fullWidth={true}
      variant="outlined"
      size="small"
      color="primary"
      onClick={handleOpenManagementCompanies}
      >
      Property Management Companies
      </Button>
      </Grid>
      )}
      </Grid>
      </Grid>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'rehab',
      card: (
      <Card variant="outlined"
      sx={{ width: "100%" }}
      sx={strategyFullWidthSx}
      >
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Rehab Expenses</b>
      </Box>
      <div>
      <Tooltip title="Estimated expenses allocated to the renovations of a property">
      <Button
      onClick={() =>
      setOpenTurnkeyRehabExpensesTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Grid>
      <Grid>
      <Box sx={strategyFlexCenterOnlySx}>
      <Suspense fallback={<LoadingSkeleton variant="chart" />}>
      <Chart
      options={rehabChartOptions}
      series={rehabChartSeries}
      type="donut"
      width="300"
      />
      </Suspense>
      </Box>
      </Grid>
      <Divider sx={strategyBlockSpacerSx} />
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center' }}
      >
      {map(rehabFields, (field) =>
      renderInputFields(field),
      )}
      </Grid>
      </Grid>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'financing',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Financing</b>
      </Box>
      <div>
      <Tooltip title="Projected fund totals an investor will need to produce in order to acquire the property">
      <Button
      onClick={() => setOpenTurnkeyFinancingTour(true)}
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Grid container sx={{
        borderRadius: "0.65rem",
        p: 1.5,
        mb: 1,
        bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
        textAlign: "center",
        border: "1px solid rgba(59, 62, 102, 0.1)",
        "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 1.25,
          py: 0.75,
          gap: 0.25,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
        "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
          minHeight: "2.6em",
          lineHeight: 1.3,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          px: 0.5,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
          color: "#070919",
          fontWeight: 700,
          fontSize: "1.15rem",
          lineHeight: 1.2,
        },
      }}>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Loan
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <div>
      fast
      </div> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={analytics.turnkey.loanAmountTurnkey}
      duration={1}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Mortgage/mo
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <div>
      normal
      </div> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={analytics.turnkey.mortgageTurnkey}
      duration={1}
      delay={2}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      DSCR
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <div>
      slow
      </div> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey.debtCoverageRatioTurnkey
      }
      duration={1}
      delay={2}
      separator=","
      decimals={2}
      suffix="x"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      </Grid>
      <Box component="p" sx={strategySubtitleCenterSx}>
      Based on your{' '}
      <b>
      {currencyFormat.format(
      analytics.turnkey.allInCostTurnkey,
      )}
      </b>{' '}
      <Tooltip title="The total amount of money this deal costs, including rehab, closing costs, points, etc.">
      <Box component="span"
      onClick={() => toggle1('All-in Cost')} sx={strategyLinkUnderlineBlueSx}>
      all-in cost
      </Box>
      </Tooltip>
      , you'll need to pay{' '}
      <b>
      {currencyFormat.format(
      analytics.turnkey.totalOutOfPocketTurnkey,
      )}
      </b>{' '}
      <Tooltip title="Total amount that the buyer has to cover with their own money.">
      <Box component="span"
      onClick={() => toggle1('Total Out-of-Pocket')} sx={strategyLinkUnderlineBlueSx}>
      out-of-pocket
      </Box>
      </Tooltip>{' '}
      to cover the downpayment, closing costs, and rehab for
      this property.
      </Box>
      <Divider sx={strategyBlockSpacerSx} />
      <Grid container>
      <Grid>
      <Grid
      container
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center' }}
      >
      {map(financingFields, (section) =>
      renderInputFields(section),
      )}
      </Grid>
      </Grid>
      </Grid>
      </CardContent>
      </Card>
      ),
      },
      {
      name: 'projection',
      card: (
      <Card variant="outlined" sx={{ width: "100%" }}>
      <Box sx={strategyMetricRowFlexSx}>
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        "& > b": { display: "block" },
        "& > small": {
          textTransform: "uppercase",
          display: "block",
          opacity: 0.3,
          fontWeight: "bold",
        },
      }}>
      <b>Value Over Time</b>
      </Box>
      <div>
      <Tooltip title="Projected paydown timelines and cashflow options available on this property">
      <Button
      onClick={() =>
      setOpenTurnkeyValueOverTimeTour(true)
      }
      sx={{
        backgroundColor: "rgba(54, 140, 255, 0.15)",
        color: "#368cff",
        borderColor: "transparent",
        "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
        "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
        height: "30px !important",
        width: "30px !important",
        lineHeight: "30px !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        border: 0,
        textAlign: "left",
      }}
      >
      <FontAwesomeIcon icon={['fal', 'info-circle']} />
      </Button>
      </Tooltip>
      </div>
      </Box>
      <Divider />
      <CardContent>
      <Box sx={{
        borderRadius: "0.65rem",
        p: 1.5,
        mb: 1,
        bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
        textAlign: "center",
        border: "1px solid rgba(59, 62, 102, 0.1)",
        "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 1.25,
          py: 0.75,
          gap: 0.25,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
        "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
          minHeight: "2.6em",
          lineHeight: 1.3,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          px: 0.5,
          boxSizing: "border-box",
        },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
        "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
          color: "#070919",
          fontWeight: 700,
          fontSize: "1.15rem",
          lineHeight: 1.2,
        },
      }}>
      <Grid container sx={{ color: "rgba(59, 62, 102, 0.5)" }}>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div data-tour="valueOverTime-step-1">
      <Box sx={strategyFieldLabelSx}>
      Property Paid off
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <Box sx={{
        display: "inline-block",
        py: 0,
        px: "0.7em",
        fontSize: "70%",
        fontWeight: 700,
        lineHeight: "21px",
        textAlign: "center",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
        borderRadius: "100%",
        textTransform: "uppercase",
        height: 10,
        color: "#fff",
        backgroundColor: "#1bc943",
        textIndent: "-999rem",
        width: 10,
        p: 0,
        boxShadow: "0 0 0 2px #fff",
        mr: "0.5rem",
      }}>
      fast
      </Box> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey
      .propertyPayOffYearsTurnkey
      }
      duration={1}
      separator=","
      decimals={1}
      suffix=" yrs"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div data-tour="valueOverTime-step-2">
      <Box sx={strategyFieldLabelSx}>
      Value at payoff
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <Box sx={{
        display: "inline-block",
        py: 0,
        px: "0.7em",
        fontSize: "70%",
        fontWeight: 700,
        lineHeight: "21px",
        textAlign: "center",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
        borderRadius: "100%",
        textTransform: "uppercase",
        height: 10,
        color: "#fff",
        backgroundColor: "#1bc943",
        textIndent: "-999rem",
        width: 10,
        p: 0,
        boxShadow: "0 0 0 2px #fff",
        mr: "0.5rem",
      }}>
      fast
      </Box> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey
      .propertyAppreciationValueTurnkey
      }
      duration={1}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div data-tour="valueOverTime-step-3">
      <Box sx={strategyFieldLabelSx}>
      Cashflow at payoff
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      {/* <Box sx={{
        display: "inline-block",
        py: 0,
        px: "0.7em",
        fontSize: "70%",
        fontWeight: 700,
        lineHeight: "21px",
        textAlign: "center",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
        borderRadius: "100%",
        textTransform: "uppercase",
        height: 10,
        color: "#fff",
        backgroundColor: "#f4772e",
        textIndent: "-999rem",
        width: 10,
        p: 0,
        boxShadow: "0 0 0 2px #fff",
        mr: "0.5rem",
      }}>
      normal
      </Box> */}
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey
      .cashFlowAtPayoffTurnkey
      }
      duration={1}
      delay={2}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      </Grid>
      <Divider sx={strategyMarginTopBottom10Sx}/>
      <Grid container sx={{ color: "rgba(59, 62, 102, 0.5)" }}>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Holding Period
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      assumptions.rentalHoldingPeriodTurnkey
      }
      duration={1}
      separator=","
      decimals={0}
      suffix=" yrs"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Value at Holding Period
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey
      .futureSaleValueAtHoldingPeriodTurnkey
      }
      duration={1}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      <Grid
      size={{ xs: 4 }}
      sx={[{ display: "flex" }, { justifyContent: "center" }]}
      >
      <div>
      <Box sx={strategyFieldLabelSx}>
      Cashflow at Holding Period
      </Box>
      <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
      <Box sx={strategyFlexPadSx}>
      <Box component="span" sx={strategyBadgeIconSx}>
      <CountUp
      start={0}
      end={
      analytics.turnkey
      .cashFlowAtHoldingPeriodTurnkey
      }
      duration={1}
      delay={2}
      separator=","
      decimals={0}
      prefix="$"
      />
      </Box>
      </Box>
      </Box>
      </div>
      </Grid>
      </Grid>
      </Box>
      {analytics.turnkey.cashFlowTurnkey >= 0 ? (
      <Box component="p"
     
      data-tour="valueOverTime-step-4" sx={strategySubtitleCenterSx}>
      If you took{' '}
      <b>
      {currencyFormat.format(
      ((analytics.turnkey.cashFlowTurnkey / 12) *
      analytics.payDownAccelerator) /
      100,
      )}
      </b>{' '}
      ({analytics.payDownAccelerator}% of the net cashflow)
      and paid it towards the{' '}
      <Tooltip title="The amount you borrowed from the lender.">
      <Box component="span" sx={strategyLinkUnderlineBlueSx}>
      loan principal
      </Box>
      </Tooltip>{' '}
      each month, this property could be paid off in{' '}
      <b>
      {analytics.turnkey.propertyPayOffYearsTurnkey.toFixed(
      1,
      )}{' '}
      years
      </b>
      {analytics.turnkey.propertyPayOffYearsTurnkey.toFixed(
      1,
      ) ===
      (assumptions.termTurnkeyType === 'Years'
      ? assumptions.termTurnkey
      : assumptions.termTurnkey / 12
      ).toFixed(1)
      ? ''
      : ` instead of ${
      assumptions.termTurnkey
      ? (assumptions.termTurnkeyType === 'Years'
      ? assumptions.termTurnkey
      : assumptions.termTurnkey / 12
      ).toFixed(1)
      : 0
      }`}
      .
      </Box>
      ) : (
      <Box component="p"
     
      data-tour="valueOverTime-step-4" sx={strategySubtitleCenterSx}>
      The cashflow of this property is less than zero, which
      means you can't take a percent of the cashflow and pay
      it towards the loan balance.
      </Box>
      )}
      <Divider sx={strategyBlockSpacerSx} />
            <Grid container>
              <Grid>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {map(projectionAssumptionsFields, (section) =>
                    renderInputFields(section),
                  )}
                  {/* <Grid size={12}>
                    <Typography id="continuous-slider" gutterBottom>
                      Paydown Accelerator (% of net cashflow)
                    </Typography>
                    <Slider value={75} />
                    <Grid container direction='row' justifyContent='space-between'>
                      <Grid>
                        <Typography id="continuous-slider" gutterBottom>
                          0%
                        </Typography>
                      </Grid>
                      <Grid>
                        <Typography>100%</Typography>
                      </Grid>
                    </Grid>
                  </Grid> */}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'cashPositioning',
      card: (
        <Card variant="outlined" sx={{ width: "100%" }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Cash Positioning</b>
            </Box>
            <div>
              <Tooltip title="Projected cash growth over one, three, five, and ten years">
                <Button
                  onClick={() =>
                    setOpenTurnkeyCashPositioningTour(true)
                  }
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div>
          </Box>
          <Divider />
          <CardContent>
            <Suspense fallback={<LoadingSkeleton variant="chart" />}>
              <Chart options={options} series={series} type="bar" />
            </Suspense>
            <Tooltip
              title={`Total Return of ${assumptions.rentalHoldingPeriodTurnkey} years`}
            >
              <Box
                sx={{
                  borderRadius: "0.65rem",
                  p: 1.5,
                  mb: 1,
                  bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
                  textAlign: "center",
                  border: "1px solid rgba(59, 62, 102, 0.1)",
                  "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.75,
                    gap: 0.25,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
                  "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
                    minHeight: "2.6em",
                    lineHeight: 1.3,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    px: 0.5,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
                    color: "#070919",
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    lineHeight: 1.2,
                  },
                }}
                onClick={() => toggle1('Total Return')}
              >
                <Grid
                  container
                  sx={{
                    justifyContent: 'space-around',
                  }}
                >
                  <Grid data-tour="cashPositioning-step-1">
                    <Typography>Total Return</Typography>
                    <Typography>
                      {currencyFormat.format(
                        analytics.turnkey
                          .totalReturnOnInvestmentTurnkey,
                      )}
                    </Typography>
                  </Grid>
                  <Grid data-tour="cashPositioning-step-2">
                    <Typography>Total Return %</Typography>
                    <Typography>
                      {percentageFormat.format(
                        analytics.turnkey.totalOutOfPocketTurnkey
                          ? analytics.turnkey
                              .totalReturnOnInvestmentTurnkey /
                              analytics.turnkey
                                .totalOutOfPocketTurnkey
                          : 0,
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Tooltip>
            <Grid container direction="row">
              <Grid size={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenAmortization}
                >
                  <Typography>Amortization</Typography>
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'disposition',
      card: (
        <Card variant="outlined" sx={{ width: "100%" }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Disposition Expenses</b>
            </Box>
            <div>
              <Tooltip title="All costs and expenses associated with sale of this property">
                <Button
                  onClick={() =>
                    setOpenTurnkeyDispositionExpensesTour(true)
                  }
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div>
          </Box>
          <Divider />
          <CardContent>
            <Grid>
              <Grid>
                <Box sx={strategyFlexCenterOnlySx}>
                  <Suspense fallback={<LoadingSkeleton variant="chart" />}>
                    <Chart
                      options={dispositionChartOptions}
                      series={dispositionChartSeries}
                      type="donut"
                      width="300"
                    />
                  </Suspense>
                </Box>
              </Grid>
              <Divider sx={strategyBlockSpacerSx} />
              <Grid>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {map(dispositionFields, (field) =>
                    renderInputFields(field),
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'comparableProperties',
      card: (
        <ComparablePropertiesCard
          basicView={props.basicView}
          compData={compData}
          onComparablePropertiesTourClick={
            toggleComparablePropertiesTour
          }
          onToggleCenter={props.toggleCenter}
          determinedComps={props.determinedComps}
          compProperties={props.compProperties}
        />
      ),
    },
    {
      name: 'piti',
      hide:
        activeOrg?.member !== 'pacemorby' &&
        activeOrg?.member !== 'astroflip',
      card: props.basicView ? null : (
        <Card variant="outlined" sx={{ width: "100%" }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Deal Sauce Summary</b>
            </Box>
            <Select
              value={dealSauceView}
              name="dealSauceView"
              onChange={(event) =>
                setDealSauceView(event.target.value)
              }
              sx={strategyFullWidthSx}
            >
              <MenuItem value="cash" key="cash">
                Cash
              </MenuItem>
              <MenuItem value="creative" key="creative">
                Creative
              </MenuItem>
            </Select>
          </Box>
          <Divider />
          <CardContent>
            {dealSauceView === 'cash' && (
              <>
                <Box sx={{
                  borderRadius: "0.65rem",
                  p: 1.5,
                  mb: 1,
                  bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
                  textAlign: "center",
                  border: "1px solid rgba(59, 62, 102, 0.1)",
                  "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.75,
                    gap: 0.25,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
                  "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
                    minHeight: "2.6em",
                    lineHeight: 1.3,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    px: 0.5,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
                    color: "#070919",
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    lineHeight: 1.2,
                  },
                }}>
                  <Grid container sx={{ color: "rgba(59, 62, 102, 0.5)" }}>
                    <Grid
                      size={{ md: 6 }}
                      sx={[{ display: "flex" }, { justifyContent: "center" }]}
                    >
                      <div>
                        <Box sx={strategyFieldLabelSx}>
                          Entry Fee
                        </Box>
                        <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
                          <Box sx={{
                              display: 'flex',
                              padding: '0.25rem',
                              alignItems: 'center',
                              minHeight: 50,
                            }}>
                            <Box component="span" sx={strategyBadgeIconSx}>
                              <CountUp
                                start={0}
                                end={cashEntryFee}
                                duration={0}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 6 }}
                      sx={[{ display: "flex" }, { justifyContent: "center" }]}
                    >
                      <div>
                        <Box sx={strategyFieldLabelSx}>
                          Max Allowable Offer
                        </Box>
                        <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
                          <Box sx={{
                              display: 'flex',
                              padding: '0.25rem',
                              alignItems: 'center',
                              minHeight: 50,
                            }}>
                            <Box component="span" sx={strategyBadgeIconSx}>
                              <CountUp
                                start={0}
                                end={
                                  analysis.afterRepairValue -
                                  cashEntryFee
                                }
                                duration={0}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                  </Grid>
                </Box>
                <Grid size={12} sx={strategyMarginTop35Sx}>
                  <Grid container direction="column" spacing={4}>
                    <ContactInformation property={property} />
                  </Grid>
                </Grid>
                <CommentsDialog
                  property={property}
                  userId={props?.session?.me?._id}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                 
                  onClick={(e) => setOpenPITICashData(true)} sx={strategyMarginTop25Sx}>
                  <Typography>View Cash Underwriting</Typography>
                </Button>
              </>
            )}
            {dealSauceView === 'creative' && (
              <>
                <Box sx={{
                  borderRadius: "0.65rem",
                  p: 1.5,
                  mb: 1,
                  bgcolor: "#f4f5fd", "html[data-color-mode=\"dark\"] &": { bgcolor: "#2a2a2a", borderColor: "rgba(255, 255, 255, 0.12)", "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87) !important" } },
                  textAlign: "center",
                  border: "1px solid rgba(59, 62, 102, 0.1)",
                  "& > .MuiGrid-container > .MuiGrid-root, &.MuiGrid-container > .MuiGrid-root": {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.75,
                    gap: 0.25,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > div, &.MuiGrid-container > .MuiGrid-root > div": { width: "100%" },
                  "& > .MuiGrid-container > .MuiGrid-root > div > div:first-of-type, &.MuiGrid-container > .MuiGrid-root > div > div:first-of-type": {
                    minHeight: "2.6em",
                    lineHeight: 1.3,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    px: 0.5,
                    boxSizing: "border-box",
                  },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:first-of-type": { color: "#4f5062", fontSize: "0.8125rem", lineHeight: 1.3 },
                  "& > .MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type, &.MuiGrid-container > .MuiGrid-root > .MuiTypography-root:last-of-type": {
                    color: "#070919",
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    lineHeight: 1.2,
                  },
                }}>
                  <Grid container sx={{ color: "rgba(59, 62, 102, 0.5)" }}>
                    <Grid
                      size={{ md: 6 }}
                      sx={[{ display: "flex" }, { justifyContent: "center" }]}
                    >
                      <div>
                        <Box sx={strategyFieldLabelSx}>
                          Entry Fee
                        </Box>
                        <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
                          <Box sx={strategyFlexPadSx}>
                            <Box component="span" sx={strategyBadgeIconSx}>
                              <CountUp
                                start={0}
                                end={creativeEntryFee}
                                duration={1}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 6 }}
                      sx={[{ display: "flex" }, { justifyContent: "center" }]}
                    >
                      <div>
                        <Box sx={strategyFieldLabelSx}>
                          PITI
                        </Box>
                        <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
                          <Box sx={strategyFlexPadSx}>
                            <Box component="span" sx={strategyBadgeIconSx}>
                              <CountUp
                                start={0}
                                end={pitiTotal}
                                duration={1}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 12 }}
                      sx={[{ display: "flex" }, { justifyContent: "center" }]}
                    >
                      <div>
                        <Box sx={strategyFieldLabelSx}>
                          % of Purchase Price
                        </Box>
                        <Box sx={[{ fontSize: "1.15rem !important" }, strategyFlexCenterSx]}>
                          <Box sx={strategyFlexPadSx}>
                            <Box component="span" sx={strategyBadgeIconSx}>
                              <CountUp
                                start={0}
                                end={
                                  analysis?.offerPrice &&
                                  analysis.offerPrice !== 0
                                    ? (creativeEntryFee /
                                        analysis.offerPrice) *
                                      100
                                    : 0
                                }
                                duration={1}
                                delay={1}
                                decimals={2}
                                decimal="."
                                suffix="%"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                  </Grid>
                </Box>
                <Grid container>
                  <Grid size={12}>
                    <Grid container direction="column" spacing={4}>
                      <ContactInformation property={property} />
                    </Grid>
                  </Grid>
                  <CommentsDialog
                    property={property}
                    userId={props?.session?.me?._id}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                   
                    onClick={(e) => setOpenPITICreativeData(true)} sx={strategyMarginTop25Sx}>
                    <Typography>
                      View Creative Underwriting
                    </Typography>
                  </Button>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'deactive',
      card: <DeactiveCard />,
    },
  ]

  const [updateProperty] = useMutation(UPDATE_PROPERTY)
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)

  return (
    <>
      <StrategyPageBase
        cards={turnkeyCards}
        strategyName="Turnkey"
        strategyKey="turnkey"
        property={property}
        activeOrg={activeOrg}
        canDrag={props.canDrag}
        publicDashboard={props.publicDashboard}
        publicImageCard={props.publicImageCard}
        session={props.session}
        renderInputFields={renderInputFields}
      />
      {map(calculationDialogs, (section) => (
        <Dialog
          key={section.label}
          open={modal1 === section.label}
          onClose={toggle1Close}
          maxWidth="lg"
          scroll="body"
          slotProps={{ paper: { sx: calcDialogPaperSx } }}
        >
          {/* <BlockUi blocking={loader} loader={<PacmanLoader color={'var(--primary)'} loading={true} />}> */}
          <Grid container spacing={0}>
            <Grid size={5}>
              <Box sx={[strategyFullHeight100Sx, {
                position: "relative",
                display: "flex",
                flexDirection: "column",
                "& > .divider": { zIndex: 7, width: "100%" },
                "& .hero-footer": { zIndex: 7 },
                "& .header-nav-wrapper": { zIndex: 27 },
                "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                borderTopLeftRadius: "inherit",
                borderTopRightRadius: "inherit",
                alignItems: "center",
                ".MuiCard-root > &": {
                  top: -1,
                  marginLeft: -1,
                  marginRight: -1,
                  marginBottom: -2,
                  zIndex: 6,
                },
                backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
              }, {
                borderRadius: "0.65rem !important",
                "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
              }]}>
                <Box sx={[strategyFlexFullWidthSx, {flexGrow:1}]}>
                  <Box sx={[strategyNavyBgSx, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 5,
                    opacity: "0.7 !important",
                  }, {
                    borderRadius: "0.65rem !important",
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]} />
                  <Box sx={[{
                    justifyContent: "center",
                    "@media (min-width: 1440px)": { justifyContent: "flex-start", textAlign: "left" },
                    textAlign: "center",
                    p: "3rem",
                  }, { position: "relative", zIndex: "7", width: "100%" }]}>
                    <Box sx={strategyFooterNoteSx}>
                      <Typography sx={strategyCardTitleSx}>
                        Calculation
                      </Typography>
                    </Box>
                    {section.calculation}
                    <Box sx={strategyFooterNoteSx}>
                      <Typography>
                        * Calculation may be a few dollars off due to
                        rounding
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={7}>
              <div>
                <Card>
                  <CardHeader title={section.label} />
                  <Divider />
                  <CardContent sx={strategyFullHeight100Sx}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid size={12} sx={strategyMarginBottom10Sx}>
                        <Card sx={strategyMetricGrayBgSx}>
                          <CardContent>
                            <Typography>
                              {section.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                      }}
                    >
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={toggle1Close}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Card>
              </div>
            </Grid>
          </Grid>
          {/* </BlockUi> */}
        </Dialog>
      ))}
      {map(financials, (section) => (
        <Dialog
          key={section.label}
          open={modal1 === section.label}
          onClose={toggle1Close}
          maxWidth="lg"
          scroll="body"
          slotProps={{ paper: { sx: calcDialogPaperSx } }}
        >
          {/* <BlockUi blocking={loader} loader={<PacmanLoader color={'var(--primary)'} loading={true} />}> */}
          <Grid container spacing={0}>
            <Grid size={5}>
              <Box sx={[strategyFullHeight100Sx, {
                position: "relative",
                display: "flex",
                flexDirection: "column",
                "& > .divider": { zIndex: 7, width: "100%" },
                "& .hero-footer": { zIndex: 7 },
                "& .header-nav-wrapper": { zIndex: 27 },
                "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                borderTopLeftRadius: "inherit",
                borderTopRightRadius: "inherit",
                alignItems: "center",
                ".MuiCard-root > &": {
                  top: -1,
                  marginLeft: -1,
                  marginRight: -1,
                  marginBottom: -2,
                  zIndex: 6,
                },
                backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
              }, {
                borderRadius: "0.65rem !important",
                "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
              }]}>
                <Box sx={[strategyFlexFullWidthSx, {flexGrow:1}]}>
                  <Box sx={[strategyNavyBgSx, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 5,
                    opacity: "0.7 !important",
                  }, {
                    borderRadius: "0.65rem !important",
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]} />
                  <Box sx={[{
                    justifyContent: "center",
                    "@media (min-width: 1440px)": { justifyContent: "flex-start", textAlign: "left" },
                    textAlign: "center",
                    p: "3rem",
                  }, { position: "relative", zIndex: "7", width: "100%" }]}>
                    <Box sx={strategyFooterNoteSx}>
                      <Typography sx={strategyCardTitleSx}>
                        Calculation
                      </Typography>
                    </Box>
                    {section.calculation}
                    <Box sx={strategyFooterNoteSx}>
                      <Typography>
                        * Calculation may be a few dollars off due to
                        rounding
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={7}>
              <div>
                <Card>
                  <CardHeader title={section.label} />
                  <Divider />
                  <CardContent sx={strategyFullHeight100Sx}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid size={12} sx={strategyMarginBottom10Sx}>
                        <Card sx={strategyMetricGrayBgSx}>
                          <CardContent>
                            <Typography>
                              {section.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                      }}
                    >
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={toggle1Close}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Card>
              </div>
            </Grid>
          </Grid>
          {/* </BlockUi> */}
        </Dialog>
      ))}
      <Dialog
        open={modal1 === 'Total Return'}
        onClose={toggle1Close}
        maxWidth="lg"
        scroll="body"
        slotProps={{ paper: { sx: calcDialogPaperSx } }}
      >
        <Grid container spacing={0}>
          <Grid size={5}>
            <Box sx={[strategyFullHeight100Sx, {
              position: "relative",
              display: "flex",
              flexDirection: "column",
              "& > .divider": { zIndex: 7, width: "100%" },
              "& .hero-footer": { zIndex: 7 },
              "& .header-nav-wrapper": { zIndex: 27 },
              "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
              borderTopLeftRadius: "inherit",
              borderTopRightRadius: "inherit",
              alignItems: "center",
              ".MuiCard-root > &": {
                top: -1,
                marginLeft: -1,
                marginRight: -1,
                marginBottom: -2,
                zIndex: 6,
              },
              backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
            }, {
              borderRadius: "0.65rem !important",
              "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
            }]}>
              <Box sx={[strategyFlexFullWidthSx, {flexGrow:1}]}>
                <Box sx={[strategyNavyBgSx, {
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: "100%",
                  zIndex: 5,
                  opacity: "0.7 !important",
                }, {
                  borderRadius: "0.65rem !important",
                  "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                }]} />
                <Box sx={[{
                  justifyContent: "center",
                  "@media (min-width: 1440px)": { justifyContent: "flex-start", textAlign: "left" },
                  textAlign: "center",
                  p: "3rem",
                }, { position: "relative", zIndex: "7", width: "100%" }]}>
                  <Box sx={strategyFooterNoteSx}>
                    <Typography sx={strategyCardTitleSx}>
                      Calculation
                    </Typography>
                  </Box>
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        Cash flow
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        {currencyFormat.format(
                          round(analytics.turnkey.cashFlowTurnkey, 0),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        +Principal Reduction
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        +
                        {currencyFormat.format(
                          round(
                            analytics.turnkey
                              .principalReductionTurnkey,
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        +Anticipated Annual Appreciation
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        +
                        {currencyFormat.format(
                          round(
                            analytics.turnkey
                              .anticipatedAnnualAppreciationTurnkey,
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={strategyMutedDividerSx} />
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyWhiteTextSx}>
                        Total Return
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyWhiteTextSx}>
                        {currencyFormat.format(
                          round(
                            analytics.turnkey
                              .totalReturnOnInvestmentTurnkey,
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        <FontAwesomeIcon
                          icon={['fal', 'divide']}
                          style={strategyIconGapStyle}
                        />
                        Total Out Of Pocket
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyMutedTextSx}>
                        <FontAwesomeIcon
                          icon={['fal', 'divide']}
                          style={strategyIconGapStyle}
                        />
                        {currencyFormat.format(
                          round(
                            analytics.turnkey.totalOutOfPocketTurnkey,
                            0,
                          ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={strategyMutedDividerSx} />
                  <Grid
                    container
                    sx={[strategyMetricRowSx, {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }]}
                  >
                    <Grid>
                      <Typography sx={strategyWhiteTextSx}>
                        Total Return %
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography sx={strategyWhiteTextSx}>
                        {percentageFormat.format(
                          analytics.turnkey.totalOutOfPocketTurnkey
                            ? analytics.turnkey
                                .totalReturnOnInvestmentTurnkey /
                                analytics.turnkey
                                  .totalOutOfPocketTurnkey
                            : 0,
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={strategyFooterNoteSx}>
                    <Typography>
                      * Calculation may be a few dollars off due to
                      rounding
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={7}>
            <div>
              <Card>
                <CardHeader title="Total Return" />
                <Divider />
                <CardContent sx={strategyFullHeight100Sx}>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Grid size={12} sx={strategyMarginBottom10Sx}>
                      <Card sx={strategyMetricGrayBgSx}>
                        <CardContent>
                          <Typography>
                            The actual rate of return of an investment
                            over a given evaluation period which
                            includes income and appreciation.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
                <Divider />
                <CardActions>
                  <Grid
                    container
                    sx={{
                      justifyContent: 'center',
                    }}
                  >
                    <Grid>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={toggle1Close}
                      >
                        Close
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </div>
          </Grid>
        </Grid>
      </Dialog>

      <AmortizationDialog
        open={openAmortization}
        onClose={handleCloseAmortization}
        tableHeaders={tableHeaders}
        amortizationData={amortizationData}
        currencyFormat2={currencyFormat2}
        assumptions={assumptions}
      />

      <Dialog
        open={openManagementCompanies}
        onClose={handleCloseManagementCompanies}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>Property Management Companies</DialogTitle>
        <DialogContent sx={strategyPadVert15Sx}>
          <Grid container direction="column" spacing={2}>
            {map(
              filter(propertyManagementCompanies, (company) =>
                includes(
                  property.propertyManagementGroups,
                  company.value,
                ),
              ),
              (section) => (
                <Grid key={section.value || section.name}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Grid>
                      <Box data-hf="avatar-image" sx={[strategyCircleGreenAvatarSx, {
                        borderRadius: "0.65rem !important",
                        "& .blur-load": { backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" },
                      }]}>
                        <Box component="img" src={section.image} alt="..." sx={{
                          maxWidth: "100%",
                          height: "100%",
                          width: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                          borderRadius: "0.29rem !important",
                        }} />
                      </Box>{' '}
                    </Grid>
                    <Grid>
                      <Typography>{section.name}</Typography>
                    </Grid>
                    <Grid>
                      <Typography>{section.url}</Typography>
                    </Grid>
                    <Grid>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        href={section.link}
                        target="_blank"
                      >
                        <Typography>View</Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              ),
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      <SubtoPITICash
        open={openPITICashData}
        onClose={() => setOpenPITICashData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />
      <SubtoPITICreative
        open={openPITICreativeData}
        onClose={() => setOpenPITICreativeData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />

      <TurnkeyStrategyTour
        isOpenTurnkeyStrategyTour={openStrategyTour}
        closeTurnkeyStrategyTour={closeStrategyTour}
      />
      <TurnkeyTargetsTour
        isOpenTurnkeyTargetsTour={openTargetsTour}
        closeTurnkeyTargetsTour={closeTargetsTour}
      />
      <TurnkeyMonthlyCashflowTour
        isOpenTurnkeyMonthlyCashflowTour={
          openTurnkeyMonthlyCashflowTour
        }
        closeTurnkeyMonthlyCashflowTour={() =>
          setOpenTurnkeyMonthlyCashflowTour(false)
        }
      />
      <TurnkeyAcquisitionExpensesTour
        isOpenTurnkeyAcquisitionExpensesTour={
          openTurnkeyAcquisitionExpensesTour
        }
        closeTurnkeyAcquisitionExpensesTour={() =>
          setOpenTurnkeyAcquisitionExpensesTour(false)
        }
      />
      <TurnkeyGrossMonthlyIncomeTour
        isOpenTurnkeyGrossMonthlyIncomeTour={
          openTurnkeyGrossMonthlyIncomeTour
        }
        closeTurnkeyGrossMonthlyIncomeTour={() =>
          setOpenTurnkeyGrossMonthlyIncomeTour(false)
        }
      />
      <TurnkeyMonthlyExpensesTour
        isOpenTurnkeyMonthlyExpensesTour={
          openTurnkeyMonthlyExpensesTour
        }
        closeTurnkeyMonthlyExpensesTour={() =>
          setOpenTurnkeyMonthlyExpensesTour(false)
        }
      />
      <TurnkeyRehabExpensesTour
        isOpenTurnkeyRehabExpensesTour={openTurnkeyRehabExpensesTour}
        closeTurnkeyRehabExpensesTour={() =>
          setOpenTurnkeyRehabExpensesTour(false)
        }
      />
      <TurnkeyFinancingTour
        isOpenTurnkeyFinancingTour={openTurnkeyFinancingTour}
        closeTurnkeyFinancingTour={() =>
          setOpenTurnkeyFinancingTour(false)
        }
      />
      <TurnkeyValueOverTimeTour
        isOpenTurnkeyValueOverTimeTour={openTurnkeyValueOverTimeTour}
        closeTurnkeyValueOverTimeTour={() =>
          setOpenTurnkeyValueOverTimeTour(false)
        }
      />
      <TurnkeyCashPositioningTour
        isOpenTurnkeyCashPositioningTour={
          openTurnkeyCashPositioningTour
        }
        closeTurnkeyCashPositioningTour={() =>
          setOpenTurnkeyCashPositioningTour(false)
        }
      />
      <TurnkeyDispositionExpensesTour
        isOpenTurnkeyDispositionExpensesTour={
          openTurnkeyDispositionExpensesTour
        }
        closeTurnkeyDispositionExpensesTour={() =>
          setOpenTurnkeyDispositionExpensesTour(false)
        }
      />
      <TurnkeyComparablePropertiesTour
        isOpenTurnkeyComparablePropertiesTour={
          openComparablePropertiesTour
        }
        closeTurnkeyComparablePropertiesTour={
          closeComparablePropertiesTour
        }
      />
    </>
  )
}

export default withSession(CenterPageTurnkey)
